// =============================================================================
// KNINE ADMIN DASHBOARD
// Internal admin system for managing customers, subscriptions, and support
// =============================================================================

import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import {
  Users, Building2, DollarSign, TrendingUp, Search, Filter, RefreshCw,
  ChevronRight, ChevronDown, ChevronLeft, Eye, Edit3, CreditCard, MessageSquare,
  Shield, LogOut, Clock, AlertTriangle, CheckCircle, XCircle, UserCheck,
  Activity, BarChart3, Settings, FileText, Download, ExternalLink, Lock,
  Plus, Trash2, MoreVertical, Calendar, Mail, Phone, Globe, ArrowUpRight,
  ArrowDownRight, Minus, User, Key, History, Zap, Target, Award, GitMerge
} from 'lucide-react';

// =============================================================================
// CONFIGURATION & API
// =============================================================================

const API_BASE = process.env.REACT_APP_API_URL || '';

const adminApiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('knine_admin_token');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    const msg = err.details ? `${err.error}: ${err.details}` : (err.error || `HTTP ${response.status}`);
    throw new Error(msg);
  }
  return response.json();
};

// =============================================================================
// ADMIN CONTEXT
// =============================================================================

const AdminContext = createContext();

const useAdmin = () => useContext(AdminContext);

const AdminProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('knine_admin_token');
    if (token) {
      adminApiCall('/admin/me')
        .then(data => setAdmin(data.admin))
        .catch(() => {
          localStorage.removeItem('knine_admin_token');
          setAdmin(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password, mfaCode) => {
    const body = { email, password };
    if (mfaCode) body.mfaCode = mfaCode;
    const data = await adminApiCall('/admin/login', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (data.mfa_required) return data;
    localStorage.setItem('knine_admin_token', data.token);
    setAdmin(data.admin);
    return data;
  };

  const logout = async () => {
    try {
      await adminApiCall('/admin/logout', { method: 'POST' });
    } catch (e) {}
    localStorage.removeItem('knine_admin_token');
    setAdmin(null);
  };

  return (
    <AdminContext.Provider value={{ admin, loading, login, logout, token: localStorage.getItem('knine_admin_token') }}>
      {children}
    </AdminContext.Provider>
  );
};

// =============================================================================
// COMPONENTS
// =============================================================================

const Card = ({ children, className = '', style = {} }) => (
  <div
    className={className}
    style={{
      background: '#1e293b',
      borderRadius: 16,
      border: '1px solid #334155',
      ...style
    }}
  >
    {children}
  </div>
);

const Button = ({ children, variant = 'primary', size = 'md', icon: Icon, onClick, disabled, style = {} }) => {
  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderRadius: 10,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s',
    border: 'none',
    opacity: disabled ? 0.5 : 1,
  };
  const variants = {
    primary: { background: 'linear-gradient(135deg, #0a66c2, #0077b5)', color: '#fff' },
    secondary: { background: '#334155', color: '#f8fafc' },
    danger: { background: '#ef4444', color: '#fff' },
    success: { background: '#10b981', color: '#fff' },
    ghost: { background: 'transparent', color: '#94a3b8' },
  };
  const sizes = {
    sm: { padding: '6px 12px', fontSize: 13 },
    md: { padding: '10px 18px', fontSize: 14 },
    lg: { padding: '14px 24px', fontSize: 16 },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{ ...baseStyle, ...variants[variant], ...sizes[size], ...style }}>
      {Icon && <Icon size={size === 'sm' ? 14 : size === 'lg' ? 20 : 16} />}
      {children}
    </button>
  );
};

const Badge = ({ children, variant = 'default', style = {} }) => {
  const variants = {
    default: { background: '#334155', color: '#94a3b8' },
    success: { background: 'rgba(16, 185, 129, 0.2)', color: '#10b981' },
    warning: { background: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b' },
    danger: { background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' },
    info: { background: 'rgba(10, 102, 194, 0.2)', color: '#0a66c2' },
  };
  return (
    <span style={{
      padding: '4px 10px',
      borderRadius: 6,
      fontSize: 12,
      fontWeight: 600,
      ...variants[variant],
      ...style
    }}>
      {children}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, subValue, trend, color = '#0a66c2' }) => (
  <Card style={{ padding: 20 }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <p style={{ margin: '0 0 4px', fontSize: 13, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</p>
        <div style={{ fontSize: 32, fontWeight: 800, color }}>{value}</div>
        {subValue && <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>{subValue}</p>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={22} style={{ color }} />
        </div>
        {trend !== undefined && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 600, color: trend > 0 ? '#10b981' : trend < 0 ? '#ef4444' : '#64748b' }}>
            {trend > 0 ? <ArrowUpRight size={14} /> : trend < 0 ? <ArrowDownRight size={14} /> : <Minus size={14} />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
    </div>
  </Card>
);

const LoadingSpinner = ({ text = 'Loading...' }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60, color: '#64748b' }}>
    <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
    <p style={{ marginTop: 12 }}>{text}</p>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const Input = ({ label, type = 'text', value, onChange, placeholder, icon: Icon, style = {} }) => (
  <div style={{ marginBottom: 16 }}>
    {label && <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500, color: '#94a3b8' }}>{label}</label>}
    <div style={{ position: 'relative' }}>
      {Icon && <Icon size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />}
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: Icon ? '12px 16px 12px 42px' : '12px 16px',
          borderRadius: 10,
          border: '1px solid #334155',
          background: '#0f172a',
          color: '#f8fafc',
          fontSize: 14,
          outline: 'none',
          ...style
        }}
      />
    </div>
  </div>
);

// =============================================================================
// LOGIN PAGE
// =============================================================================

const AdminLogin = () => {
  const { login } = useAdmin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password, mfaRequired ? mfaCode : undefined);
      if (result?.mfa_required) {
        setMfaRequired(true);
        setError('');
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', padding: 20 }}>
      <Card style={{ padding: 40, width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ width: 60, height: 60, borderRadius: 16, background: 'linear-gradient(135deg, #0a66c2, #10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <Shield size={28} style={{ color: '#fff' }} />
          </div>
          <h1 style={{ margin: '0 0 8px', fontSize: 24, fontWeight: 800, color: '#f8fafc' }}>kNine Admin</h1>
          <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>
            {mfaRequired ? 'Enter your authenticator code' : 'Internal administration portal'}
          </p>
        </div>

        {error && (
          <div style={{ padding: 12, marginBottom: 20, borderRadius: 10, background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#ef4444', fontSize: 14 }}>
            {error}
          </div>
        )}

        {!mfaRequired ? (
          <form onSubmit={handleSubmit}>
            <Input label="Email" type="email" value={email} onChange={setEmail} placeholder="admin@knine.ai" icon={Mail} />
            <Input label="Password" type="password" value={password} onChange={setPassword} placeholder="Enter password" icon={Lock} />
            <Button variant="primary" onClick={handleSubmit} disabled={loading} style={{ width: '100%', marginTop: 8 }}>
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        ) : (
          <div>
            <div style={{ padding: '12px 16px', borderRadius: 10, background: 'rgba(10,102,194,0.1)', border: '1px solid rgba(10,102,194,0.3)', marginBottom: 20, fontSize: 13, color: '#93c5fd' }}>
              Open your authenticator app (Google Authenticator, Authy, etc.) and enter the 6-digit code for <strong>kNine Admin</strong>.
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500, color: '#94a3b8' }}>6-Digit Code</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={mfaCode}
                onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                placeholder="123456"
                autoFocus
                style={{ width: '100%', padding: '14px', borderRadius: 10, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 24, letterSpacing: 8, textAlign: 'center', boxSizing: 'border-box' }}
              />
            </div>
            <Button variant="primary" onClick={handleSubmit} disabled={loading || mfaCode.length !== 6} style={{ width: '100%' }}>
              {loading ? 'Verifying...' : 'Verify Code'}
            </Button>
            <button onClick={() => { setMfaRequired(false); setMfaCode(''); setError(''); }}
              style={{ marginTop: 12, background: 'none', border: 'none', color: '#64748b', fontSize: 13, cursor: 'pointer', width: '100%' }}>
              Back to login
            </button>
          </div>
        )}

        <p style={{ textAlign: 'center', marginTop: 24, fontSize: 12, color: '#64748b' }}>
          Authorized personnel only. All actions are logged.
        </p>
      </Card>
    </div>
  );
};

// =============================================================================
// DASHBOARD
// =============================================================================

// =============================================================================
// ACCOUNTS OVERVIEW — HubSpot deal data, subscription status, org toggles
// =============================================================================

const AccountsOverview = () => {
  const [overview, setOverview] = useState(null);  // { accounts, payingCustomers, trialAccounts, summary }
  const [pipeline, setPipeline] = useState(null);  // HubSpot open deals
  const [loading, setLoading] = useState(true);
  const [pipelineLoading, setPipelineLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('paying'); // 'paying' | 'trials'
  const [toggling, setToggling] = useState(null);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncError, setSyncError] = useState(null);

  useEffect(() => {
    loadAccounts();
    loadSyncStatus();
    loadPipeline();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const d = await adminApiCall('/admin/accounts-overview');
      setOverview(d);
    } catch (err) {
      console.error('Accounts overview error:', err);
    }
    setLoading(false);
  };

  const loadSyncStatus = async () => {
    try {
      const d = await adminApiCall('/admin/hubspot/sync/status');
      setSyncStatus(d.lastSync || null);
    } catch (_) {}
  };

  const loadPipeline = async () => {
    setPipelineLoading(true);
    try {
      const d = await adminApiCall('/admin/hubspot/pipeline');
      setPipeline(d);
    } catch (_) {}
    setPipelineLoading(false);
  };

  const handleSync = async () => {
    setSyncing(true); setSyncError(null);
    try {
      const result = await adminApiCall('/admin/hubspot/sync', { method: 'POST' });
      setSyncStatus({ syncedAt: result.syncedAt, synced: result.synced, skipped: result.skipped, hasErrors: result.errors?.length > 0 });
      await Promise.all([loadAccounts(), loadPipeline()]);
    } catch (err) { setSyncError(err.message); }
    setSyncing(false);
  };

  const handleToggle = async (orgId, orgName, currentStatus) => {
    if (!window.confirm(`${currentStatus === 'active' ? 'Suspend' : 'Reactivate'} "${orgName}"?`)) return;
    setToggling(orgId);
    try {
      const d = await adminApiCall(`/admin/customers/${orgId}/toggle`, { method: 'POST' });
      setOverview(prev => {
        if (!prev) return prev;
        const updateOrg = list => list.map(a => a.id === orgId ? { ...a, subscriptionStatus: d.subscriptionStatus } : a);
        return { ...prev, accounts: updateOrg(prev.accounts), payingCustomers: updateOrg(prev.payingCustomers), trialAccounts: updateOrg(prev.trialAccounts) };
      });
    } catch (err) { alert('Failed: ' + err.message); }
    setToggling(null);
  };

  const fmt = (cents) => (cents && cents > 0) ? '$' + Math.round(cents / 100).toLocaleString() : '—';
  const fmtUsd = (usd) => (usd && usd > 0) ? '$' + parseFloat(usd).toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
  const fmtAgo = (d) => {
    if (!d) return null;
    const mins = Math.round((Date.now() - new Date(d)) / 60000);
    if (mins < 2) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
    return `${Math.round(mins / 1440)}d ago`;
  };
  const getDeal = (a) => {
    if (!a.hubspot_deal_data) return null;
    try {
      const data = typeof a.hubspot_deal_data === 'string' ? JSON.parse(a.hubspot_deal_data) : a.hubspot_deal_data;
      return Array.isArray(data) ? data[0] : data;
    } catch { return null; }
  };
  const statusColor = { active: '#10b981', past_due: '#f59e0b', suspended: '#ef4444', trial: '#8b5cf6', free: '#64748b', churned: '#94a3b8', merged: '#475569' };

  const summary = overview?.summary || {};
  const displayList = (activeSection === 'paying' ? (overview?.payingCustomers || []) : (overview?.trialAccounts || []))
    .filter(a => !search || a.name?.toLowerCase().includes(search.toLowerCase()) || a.primary_contact_email?.toLowerCase().includes(search.toLowerCase()));

  const now = new Date();
  const monthName = now.toLocaleString('default', { month: 'long' });

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: '0 0 4px', fontSize: 28, fontWeight: 800, color: '#f8fafc' }}>Accounts</h1>
          <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>HubSpot synced · paying customers vs. trial pipeline</p>
          {syncStatus && (
            <p style={{ margin: '4px 0 0', fontSize: 12, color: syncStatus.hasErrors ? '#f59e0b' : '#10b981' }}>
              {syncStatus.hasErrors ? '⚠' : '✓'} Last HubSpot sync {fmtAgo(syncStatus.syncedAt)} — {syncStatus.synced} orgs updated
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" icon={RefreshCw} onClick={loadAccounts} disabled={loading || syncing}>Refresh</Button>
          <Button variant="primary" icon={syncing ? RefreshCw : Zap} onClick={handleSync} disabled={syncing || loading}
            style={{ background: syncing ? '#334155' : 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none' }}>
            {syncing ? 'Syncing…' : 'Sync HubSpot'}
          </Button>
        </div>
      </div>

      {syncError && (
        <div style={{ padding: '10px 16px', borderRadius: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', marginBottom: 16, color: '#fca5a5', fontSize: 13 }}>
          HubSpot sync failed: {syncError}
        </div>
      )}

      {/* Top stat row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Paying Customers', value: summary.payingCount ?? '—', color: '#10b981', sub: `${summary.pastDueCount || 0} past due` },
          { label: 'Monthly Recurring', value: summary.totalMRR ? '$' + Math.round(summary.totalMRR / 100).toLocaleString() : '—', color: '#a78bfa', sub: summary.totalARR ? 'ARR $' + Math.round(summary.totalARR / 100).toLocaleString() : '' },
          { label: `${monthName} Pipeline`, value: pipeline ? fmtUsd(pipeline.totalValue) : '…', color: '#0a66c2', sub: pipeline ? `${pipeline.totalDeals} open deals` : 'loading' },
          { label: `New Trials ${monthName}`, value: summary.newTrialsMTD ?? '—', color: '#f59e0b', sub: `${summary.trialCount || 0} total trials` },
          { label: 'Weighted Forecast', value: pipeline ? fmtUsd(pipeline.weightedValue) : '…', color: '#06b6d4', sub: 'probability-adjusted' },
        ].map(s => (
          <Card key={s.label} style={{ padding: '16px 18px' }}>
            <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: s.color, marginBottom: 2 }}>{s.value}</div>
            {s.sub && <div style={{ fontSize: 11, color: '#64748b' }}>{s.sub}</div>}
          </Card>
        ))}
      </div>

      {/* HubSpot pipeline breakdown (shown when data arrives) */}
      {pipeline?.byStage?.length > 0 && (
        <Card style={{ padding: 16, marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Open Pipeline by Stage</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {pipeline.byStage.map(s => (
              <div key={s.stage} style={{ padding: '8px 14px', borderRadius: 8, background: '#0f172a', border: '1px solid #334155', flex: '1 1 180px' }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>{s.stage || 'Unknown Stage'}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#f8fafc' }}>{fmtUsd(s.value)}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{s.count} deal{s.count !== 1 ? 's' : ''}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Section toggle + search */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <button onClick={() => setActiveSection('paying')} style={{
          padding: '7px 18px', borderRadius: 8, border: '1px solid',
          borderColor: activeSection === 'paying' ? '#10b981' : '#334155',
          background: activeSection === 'paying' ? 'rgba(16,185,129,0.15)' : 'transparent',
          color: activeSection === 'paying' ? '#10b981' : '#94a3b8',
          fontSize: 13, cursor: 'pointer', fontWeight: 600,
        }}>
          Paying Customers ({summary.payingCount ?? '…'})
        </button>
        <button onClick={() => setActiveSection('trials')} style={{
          padding: '7px 18px', borderRadius: 8, border: '1px solid',
          borderColor: activeSection === 'trials' ? '#f59e0b' : '#334155',
          background: activeSection === 'trials' ? 'rgba(245,158,11,0.15)' : 'transparent',
          color: activeSection === 'trials' ? '#f59e0b' : '#94a3b8',
          fontSize: 13, cursor: 'pointer', fontWeight: 600,
        }}>
          Trial Accounts ({summary.trialCount ?? '…'})
        </button>
        <input
          value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email…"
          style={{ flex: 1, padding: '7px 12px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 13 }}
        />
      </div>

      {loading ? <LoadingSpinner text="Loading accounts…" /> : (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#0f172a' }}>
                {['Organization', 'Status', 'Plan', 'HubSpot Deal', 'Contact', 'Renewal', 'ARR', 'MRR', 'Users', 'Access'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayList.length === 0 && (
                <tr><td colSpan={10} style={{ padding: 40, textAlign: 'center', color: '#64748b', fontSize: 14 }}>
                  {activeSection === 'paying' ? 'No paying customers yet. Sync HubSpot or upgrade a trial account.' : 'No trial accounts found.'}
                </td></tr>
              )}
              {displayList.map((acc, i) => {
                const deal = getDeal(acc);
                const isExpanded = expandedId === acc.id;
                return (
                  <React.Fragment key={acc.id}>
                    <tr onClick={() => setExpandedId(isExpanded ? null : acc.id)} style={{
                      borderTop: i === 0 ? 'none' : '1px solid #1e293b',
                      background: isExpanded ? 'rgba(10,102,194,0.06)' : 'transparent',
                      cursor: 'pointer',
                    }}>
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ fontWeight: 600, color: '#f8fafc', fontSize: 14 }}>{acc.name}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{acc.primary_contact_email || '—'}</div>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{ padding: '3px 9px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                          background: `${statusColor[acc.subscriptionStatus] || '#64748b'}22`,
                          color: statusColor[acc.subscriptionStatus] || '#64748b' }}>
                          {acc.subscriptionStatus || 'unknown'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 13, color: '#f8fafc', textTransform: 'capitalize' }}>{acc.subscriptionTier || 'free'}</td>
                      <td style={{ padding: '12px 14px', fontSize: 12 }}>
                        {deal
                          ? <><div style={{ color: '#f8fafc', fontWeight: 500 }}>{deal.name || deal.properties?.dealname || 'Deal linked'}</div>
                              <div style={{ fontSize: 11, color: '#64748b' }}>{deal.stage || deal.properties?.dealstage || '—'}</div></>
                          : acc.hubspot_company_id
                            ? <span style={{ color: '#64748b' }}>Linked, no deal</span>
                            : <span style={{ color: '#475569', fontStyle: 'italic' }}>Not linked</span>}
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 13, color: '#f8fafc' }}>{acc.primary_contact_name || '—'}</td>
                      <td style={{ padding: '12px 14px', fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>{fmtDate(acc.subscriptionRenewalDate)}</td>
                      <td style={{ padding: '12px 14px', fontSize: 13, color: '#10b981', fontWeight: 600 }}>
                        {acc.mrr > 0 ? fmt(acc.mrr * 12) : (deal ? fmtUsd(deal.amount || deal.properties?.amount) : '—')}
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 13, color: acc.mrr > 0 ? '#a78bfa' : '#475569', fontWeight: acc.mrr > 0 ? 600 : 400 }}>
                        {fmt(acc.mrr)}
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 13, color: '#f8fafc' }}>{acc.active_users}</td>
                      <td style={{ padding: '12px 14px' }} onClick={e => e.stopPropagation()}>
                        <button onClick={() => handleToggle(acc.id, acc.name, acc.subscriptionStatus)} disabled={toggling === acc.id} style={{
                          padding: '5px 11px', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 600,
                          cursor: toggling === acc.id ? 'not-allowed' : 'pointer',
                          background: acc.subscriptionStatus === 'active' ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)',
                          color: acc.subscriptionStatus === 'active' ? '#ef4444' : '#10b981',
                          opacity: toggling === acc.id ? 0.5 : 1,
                        }}>
                          {toggling === acc.id ? '…' : acc.subscriptionStatus === 'active' ? 'Suspend' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr style={{ background: 'rgba(10,102,194,0.04)' }}>
                        <td colSpan={10} style={{ padding: '0 14px 16px' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                            {[
                              { label: 'Org ID', value: acc.id },
                              { label: 'HubSpot Company ID', value: acc.hubspot_company_id || 'Not linked' },
                              { label: 'Created', value: fmtDate(acc.createdAt) },
                              { label: 'Credits Remaining', value: acc.credits_remaining?.toLocaleString() },
                              { label: 'Deal Name', value: deal?.name || deal?.properties?.dealname || '—' },
                              { label: 'Deal Amount', value: deal ? fmtUsd(deal.amount || deal.properties?.amount) : '—' },
                              { label: 'Deal Stage', value: deal?.stage || deal?.properties?.dealstage || '—' },
                              { label: 'Close Date', value: fmtDate(deal?.closeDate || deal?.properties?.closedate) },
                              { label: 'MRR', value: fmt(acc.mrr) },
                              { label: 'ARR', value: fmt((acc.mrr || 0) * 12) },
                              { label: 'Billing Interval', value: acc.billing_interval || 'annual' },
                              { label: 'HubSpot Synced', value: acc.hubspot_deal_synced_at ? fmtAgo(acc.hubspot_deal_synced_at) : 'Never' },
                            ].map(f => (
                              <div key={f.label} style={{ padding: '10px 12px', background: '#0f172a', borderRadius: 8 }}>
                                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.5 }}>{f.label}</div>
                                <div style={{ fontSize: 13, color: '#f8fafc', fontFamily: f.label.includes('ID') ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>{f.value || '—'}</div>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

const DashboardOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const result = await adminApiCall('/admin/dashboard');
      setData(result);
    } catch (err) {
      console.error('Dashboard load error:', err);
    }
    setLoading(false);
  };

  if (loading) return <LoadingSpinner text="Loading dashboard..." />;

  return (
    <div>
      <div className="kubi-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, gap: 12 }}>
        <div>
          <h1 className="kubi-page-title" style={{ margin: '0 0 4px', fontSize: 28, fontWeight: 800, color: '#f8fafc' }}>Dashboard</h1>
          <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>Overview of kNine platform metrics</p>
        </div>
        <Button variant="secondary" icon={RefreshCw} onClick={loadDashboard}>Refresh</Button>
      </div>

      {data && (
        <>
          <div className="kubi-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
            <StatCard icon={Building2} label="Total Customers" value={data.overview.totalCustomers} color="#0a66c2" />
            <StatCard icon={Users} label="Active Customers" value={data.overview.activeCustomers} subValue={`${data.overview.trialCustomers} in trial`} color="#10b981" />
            <StatCard icon={DollarSign} label="MRR" value={data.revenue.mrrFormatted} subValue={`ARR: ${data.revenue.arrFormatted}`} color="#f59e0b" />
            <StatCard icon={UserCheck} label="Active Users Today" value={data.overview.activeUsersToday} subValue={`${data.overview.totalUsers} total users`} color="#8b5cf6" />
          </div>

          <div className="kubi-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
            <StatCard icon={Plus} label="New This Month" value={data.overview.newThisMonth} color="#10b981" />
            <StatCard icon={XCircle} label="Churned This Month" value={data.overview.churnedThisMonth} color="#ef4444" />
            <StatCard icon={TrendingUp} label="Net Growth" value={data.overview.newThisMonth - data.overview.churnedThisMonth} color={data.overview.newThisMonth > data.overview.churnedThisMonth ? '#10b981' : '#ef4444'} />
            <StatCard icon={Target} label="Conversion Rate" value={data.overview.trialCustomers > 0 ? `${((data.overview.activeCustomers / (data.overview.activeCustomers + data.overview.trialCustomers)) * 100).toFixed(0)}%` : 'N/A'} color="#0a66c2" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <Card style={{ padding: 24 }}>
              <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Subscription Breakdown</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {data.tierBreakdown.map(tier => (
                  <div key={tier.subscriptionTier} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 12, background: '#0f172a', borderRadius: 10 }}>
                    <span style={{ fontWeight: 500, color: '#f8fafc', textTransform: 'capitalize' }}>{tier.subscriptionTier || 'Unknown'}</span>
                    <Badge variant={tier.subscriptionTier === 'enterprise' ? 'success' : tier.subscriptionTier === 'trial' ? 'warning' : 'info'}>{tier.count}</Badge>
                  </div>
                ))}
              </div>
            </Card>

            <Card style={{ padding: 24 }}>
              <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Recent Signups</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.recentSignups.slice(0, 5).map(org => (
                  <div key={org.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 12, background: '#0f172a', borderRadius: 10 }}>
                    <div>
                      <div style={{ fontWeight: 600, color: '#f8fafc' }}>{org.name}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>{org.user_count} users • {new Date(org.createdAt).toLocaleDateString()}</div>
                    </div>
                    <Badge variant={org.subscriptionTier === 'trial' ? 'warning' : 'success'}>{org.subscriptionTier}</Badge>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

// =============================================================================
// CUSTOMERS LIST
// =============================================================================

// =============================================================================
// PROVISION CUSTOMER MODAL
// =============================================================================

// HubSpot company search widget — shared between provision modal and link UI
const HubSpotCompanySearch = ({ onSelect, placeholder = 'Search HubSpot companies...' }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = React.useRef(null);

  const search = (q) => {
    if (q.trim().length < 2) { setResults([]); setOpen(false); return; }
    setSearching(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const d = await adminApiCall(`/admin/hubspot/companies/search?q=${encodeURIComponent(q.trim())}`);
        setResults(d.companies || []);
        setOpen(true);
      } catch (e) {
        setResults([]);
      }
      setSearching(false);
    }, 350);
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); search(e.target.value); }}
          onFocus={() => results.length > 0 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder={placeholder}
          style={{ width: '100%', padding: '10px 12px 10px 32px', borderRadius: 8, border: '1px solid #0a66c2', background: '#0f172a', color: '#f8fafc', fontSize: 13, boxSizing: 'border-box' }}
        />
        {searching && <RefreshCw size={14} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#64748b', animation: 'spin 1s linear infinite' }} />}
      </div>
      {open && results.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: '#1e293b', border: '1px solid #334155', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', marginTop: 4, maxHeight: 280, overflowY: 'auto' }}>
          {results.map(c => (
            <div
              key={c.id}
              onMouseDown={() => { onSelect(c); setQuery(c.name); setOpen(false); }}
              style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              onMouseEnter={e => e.currentTarget.style.background = '#334155'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div>
                <div style={{ fontWeight: 600, color: '#f8fafc', fontSize: 13 }}>{c.name}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                  ID: {c.id}{c.domain ? ` · ${c.domain}` : ''}{c.location ? ` · ${c.location}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                {c.alreadyLinked && <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: 'rgba(245,158,11,0.2)', color: '#f59e0b' }}>Already linked</span>}
                {!c.alreadyLinked && <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: 'rgba(16,185,129,0.2)', color: '#10b981' }}>Available</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      {open && results.length === 0 && query.length >= 2 && !searching && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '12px 14px', marginTop: 4, fontSize: 13, color: '#64748b' }}>
          No HubSpot companies found for "{query}"
        </div>
      )}
    </div>
  );
};

const ProvisionCustomerModal = ({ onClose, onSuccess }) => {
  const [selectedCompany, setSelectedCompany] = useState(null); // { id, name, domain, alreadyLinked }
  const [orgName, setOrgName] = useState('');
  const [plan, setPlan] = useState('professional');
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [primaryUserEmail, setPrimaryUserEmail] = useState('');
  const [primaryUserName, setPrimaryUserName] = useState('');
  const [additionalSeats, setAdditionalSeats] = useState(0);
  const [hasTrackerAddon, setHasTrackerAddon] = useState(false);
  const [hasFeeRecoveryAddon, setHasFeeRecoveryAddon] = useState(false);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 13, boxSizing: 'border-box' };
  const labelStyle = { display: 'block', marginBottom: 4, fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' };

  const PLAN_CREDITS = { free: '50', starter: '2,000', professional: '5,000', business: '10,000', enterprise: 'Unlimited' };
  const monthlyCredits = { free: 4, starter: 167, professional: 417, business: 833, enterprise: null };

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    if (!orgName) setOrgName(company.name); // auto-fill if blank
  };

  const handleSubmit = async () => {
    if (!selectedCompany) {
      setError('Select a HubSpot company first — the record must exist in HubSpot before provisioning');
      return;
    }
    if (!orgName || !primaryUserEmail || !primaryUserName) {
      setError('Organization name, primary user email, and name are required');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await adminApiCall('/admin/customers', {
        method: 'POST',
        body: JSON.stringify({
          orgName, plan, billingInterval,
          primaryUserEmail, primaryUserName,
          additionalSeats: parseInt(additionalSeats) || 0,
          hasTrackerAddon, hasFeeRecoveryAddon,
          hubspotCompanyId: selectedCompany.id,
          notes: notes || undefined,
        }),
      });
      setResult(data);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  if (result) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
        <Card style={{ padding: 32, width: 520 }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <CheckCircle size={48} style={{ color: '#10b981', marginBottom: 12 }} />
            <h3 style={{ margin: '0 0 8px', color: '#f8fafc', fontSize: 20 }}>Customer Provisioned!</h3>
            <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}>
              {result.emailSent ? 'Invite email sent to ' + primaryUserEmail : 'Account created (invite email failed — resend manually)'}
            </p>
          </div>
          <div style={{ background: '#0f172a', borderRadius: 10, padding: 16, marginBottom: 16, fontSize: 13 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div><span style={{ color: '#64748b' }}>HubSpot: </span><span style={{ color: '#f8fafc', fontWeight: 600 }}>{selectedCompany?.name}</span></div>
              <div><span style={{ color: '#64748b' }}>Company ID: </span><span style={{ color: '#f8fafc', fontFamily: 'monospace' }}>{selectedCompany?.id}</span></div>
              <div><span style={{ color: '#64748b' }}>Plan: </span><span style={{ color: '#f8fafc', fontWeight: 600 }}>{plan}</span></div>
              <div><span style={{ color: '#64748b' }}>Billing: </span><span style={{ color: '#f8fafc', fontWeight: 600 }}>{billingInterval}</span></div>
              <div><span style={{ color: '#64748b' }}>Initial credits: </span><span style={{ color: '#10b981', fontWeight: 600 }}>{result.initialCredits?.toLocaleString()}</span></div>
              {result.monthlyAllowance && (
                <div><span style={{ color: '#64748b' }}>Monthly reset: </span><span style={{ color: '#10b981', fontWeight: 600 }}>{result.monthlyAllowance?.toLocaleString()}</span></div>
              )}
              <div><span style={{ color: '#64748b' }}>Next renewal: </span><span style={{ color: '#f8fafc' }}>{new Date(result.renewalDate).toLocaleDateString()}</span></div>
            </div>
          </div>
          {!result.emailSent && (
            <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', color: '#fbbf24', fontSize: 12, marginBottom: 16 }}>
              <strong>Invite URL:</strong> <a href={result.inviteUrl} style={{ color: '#93c5fd', wordBreak: 'break-all' }}>{result.inviteUrl}</a>
            </div>
          )}
          <Button variant="primary" onClick={() => { onSuccess(); onClose(); }} style={{ width: '100%' }}>View Customer</Button>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto' }}>
      <Card style={{ padding: 28, width: 580, margin: '24px auto' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: '#f8fafc' }}>Provision Customer</h3>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748b' }}>The company must already exist in HubSpot. Search and select it first — no duplicates will be created.</p>

        {error && <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', fontSize: 13, marginBottom: 16 }}>{error}</div>}

        {/* Step 1: HubSpot company selection (required) */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ ...labelStyle, color: selectedCompany ? '#10b981' : '#f59e0b' }}>
            {selectedCompany ? '✓ HubSpot Company Selected' : 'Step 1 — Find HubSpot Company (required) *'}
          </label>
          {selectedCompany ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)' }}>
              <CheckCircle size={16} style={{ color: '#10b981', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: '#f8fafc', fontSize: 14 }}>{selectedCompany.name}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>HubSpot ID: {selectedCompany.id}{selectedCompany.domain ? ` · ${selectedCompany.domain}` : ''}</div>
              </div>
              <button onClick={() => { setSelectedCompany(null); setOrgName(''); }}
                style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 12 }}>Change</button>
            </div>
          ) : (
            <>
              <HubSpotCompanySearch onSelect={handleCompanySelect} placeholder="Type company name to search HubSpot..." />
              <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 4 }}>Company must be closed-won in HubSpot before provisioning kNine access.</div>
            </>
          )}
        </div>

        {/* Step 2: Org details — only shown after company selected */}
        {selectedCompany && (
          <>
            <div style={{ borderTop: '1px solid #334155', paddingTop: 16, marginBottom: 14 }}>
              <label style={{ ...labelStyle, marginBottom: 10 }}>Step 2 — Account Details</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Organization Name (in kNine) *</label>
                  <input value={orgName} onChange={e => setOrgName(e.target.value)} placeholder={selectedCompany.name} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Primary User Name *</label>
                  <input value={primaryUserName} onChange={e => setPrimaryUserName(e.target.value)} placeholder="Jane Smith" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Primary User Email *</label>
                  <input type="email" value={primaryUserEmail} onChange={e => setPrimaryUserEmail(e.target.value)} placeholder="jane@acme.com" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Plan</label>
                  <select value={plan} onChange={e => setPlan(e.target.value)} style={inputStyle}>
                    <option value="starter">Starter — 2,000 credits/yr</option>
                    <option value="professional">Professional — 5,000 credits/yr</option>
                    <option value="business">Business — 10,000 credits/yr</option>
                    <option value="enterprise">Enterprise — Unlimited</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Billing Interval</label>
                  <select value={billingInterval} onChange={e => setBillingInterval(e.target.value)} style={inputStyle}>
                    <option value="monthly">Monthly (credits reset each month)</option>
                    <option value="annual">Annual (all credits upfront)</option>
                  </select>
                </div>
              </div>
            </div>

            <div style={{ background: 'rgba(10,102,194,0.08)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#64748b' }}>
              {billingInterval === 'monthly' ? (
                <span>Monthly billing: <strong style={{ color: '#10b981' }}>{monthlyCredits[plan] || '?'} credits/month</strong> — unused credits expire at renewal (no rollover). Annual total: <strong style={{ color: '#f8fafc' }}>{PLAN_CREDITS[plan]}</strong></span>
              ) : (
                <span>Annual billing: <strong style={{ color: '#10b981' }}>{PLAN_CREDITS[plan]} credits</strong> granted upfront.</span>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Additional Seats</label>
                <input type="number" min="0" value={additionalSeats} onChange={e => setAdditionalSeats(e.target.value)} style={inputStyle} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, justifyContent: 'flex-end' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#94a3b8', cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" checked={hasTrackerAddon} onChange={e => setHasTrackerAddon(e.target.checked)} />
                  Tracker Add-on
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#94a3b8', cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" checked={hasFeeRecoveryAddon} onChange={e => setHasFeeRecoveryAddon(e.target.checked)} />
                  Fee Recovery Add-on
                </label>
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Internal Notes (optional)</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Closed won via HubSpot quote, signed 2026-05-04" rows={2}
                style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 12 }}>
          <Button variant="secondary" onClick={onClose} style={{ flex: 1 }} disabled={loading}>Cancel</Button>
          <Button
            variant="primary" onClick={handleSubmit}
            disabled={loading || !selectedCompany || !orgName || !primaryUserEmail || !primaryUserName}
            style={{ flex: 1 }}
          >
            {loading ? 'Provisioning...' : 'Provision Customer'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

// =============================================================================
// ADD USER MODAL (for existing org)
// =============================================================================

const AddUserModal = ({ orgId, orgName, orgCredits, onClose, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('user');
  const [allocationType, setAllocationType] = useState('none');
  const [allocationValue, setAllocationValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null); // { userId, inviteUrl, emailSent }

  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 13, boxSizing: 'border-box' };

  const handleSubmit = async () => {
    if (!email || !name) { setError('Email and name are required'); return; }
    setLoading(true); setError(null);
    try {
      const result = await adminApiCall(`/admin/customers/${orgId}/users`, {
        method: 'POST',
        body: JSON.stringify({ email, name, role }),
      });
      // Set credit allocation if specified
      if (allocationType && allocationType !== 'none' && result.userId) {
        try {
          await adminApiCall(`/admin/customers/${orgId}/user-limits`, {
            method: 'POST',
            body: JSON.stringify({
              userId: result.userId,
              allocationType,
              allocationValue: Number(allocationValue) || null,
              reason: 'Set at account creation'
            })
          });
        } catch (_) {}
      }
      setDone(result);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const copyLink = () => {
    if (done?.inviteUrl) navigator.clipboard.writeText(done.inviteUrl);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <Card style={{ padding: 24, width: 460, maxHeight: '90vh', overflowY: 'auto' }}>
        {done ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <CheckCircle size={32} style={{ color: '#10b981', flexShrink: 0 }} />
              <div>
                <h4 style={{ margin: 0, color: '#f8fafc' }}>User Added</h4>
                <p style={{ margin: '2px 0 0', fontSize: 13, color: '#64748b' }}>
                  {done.emailSent ? `Invite email sent to ${email}` : `Email not sent — copy the invite link below`}
                </p>
              </div>
            </div>
            {done.inviteUrl && (
              <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6, fontWeight: 600 }}>INVITE LINK (expires in 7 days)</div>
                <div style={{ fontSize: 12, color: '#94a3b8', wordBreak: 'break-all', marginBottom: 8 }}>{done.inviteUrl}</div>
                <button onClick={copyLink} style={{ fontSize: 12, padding: '4px 12px', borderRadius: 6, border: '1px solid #334155', background: 'none', color: '#818cf8', cursor: 'pointer' }}>
                  Copy Link
                </button>
              </div>
            )}
            <Button variant="primary" onClick={() => { onSuccess(); onClose(); }} style={{ width: '100%' }}>Done</Button>
          </div>
        ) : (
          <>
            <h3 style={{ margin: '0 0 4px', color: '#f8fafc', fontSize: 18 }}>Add User to {orgName}</h3>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748b' }}>An invite email will be sent with a link to set their password.</p>
            {error && <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', color: '#fca5a5', fontSize: 12, marginBottom: 12 }}>{error}</div>}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Full Name *</label>
                <input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith" style={inputStyle} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Email *</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@company.com" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Role</label>
                <select value={role} onChange={e => setRole(e.target.value)} style={inputStyle}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="owner">Owner / Super User</option>
                  <option value="viewer">Viewer (read-only)</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Credit Limit</label>
                <select value={allocationType} onChange={e => setAllocationType(e.target.value)} style={inputStyle}>
                  <option value="none">Unlimited</option>
                  <option value="fixed">Fixed credits</option>
                  <option value="percentage">% of org pool</option>
                </select>
              </div>
            </div>

            {allocationType !== 'none' && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>
                  {allocationType === 'fixed' ? 'Credits per period' : 'Percentage (%)'}
                  {orgCredits != null && allocationType === 'fixed' && (
                    <span style={{ fontWeight: 400, color: '#64748b', marginLeft: 8 }}>org pool: {orgCredits.toLocaleString()} credits</span>
                  )}
                </label>
                <input
                  type="number" min="0"
                  value={allocationValue}
                  onChange={e => setAllocationValue(e.target.value)}
                  placeholder={allocationType === 'fixed' ? 'e.g. 500' : 'e.g. 25'}
                  style={inputStyle}
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              <Button variant="secondary" onClick={onClose} style={{ flex: 1 }}>Cancel</Button>
              <Button variant="primary" onClick={handleSubmit} disabled={loading || !email || !name} style={{ flex: 1 }}>
                {loading ? 'Adding...' : 'Add & Invite'}
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

const ProvisionTrialButton = ({ customerId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleProvision = async () => {
    if (!window.confirm('Set this org to Free Trial with 50 shared credits? This will reset credits to 50.')) return;
    setLoading(true);
    try {
      await adminApiCall(`/admin/customers/${customerId}/provision-trial`, { method: 'POST', body: JSON.stringify({}) });
      setDone(true);
      await onSuccess();
    } catch (err) {
      alert('Failed: ' + err.message);
    }
    setLoading(false);
  };

  return (
    <Button
      variant="secondary"
      onClick={handleProvision}
      disabled={loading}
      style={{ fontSize: 12, padding: '5px 14px', background: done ? 'rgba(16,185,129,0.1)' : undefined, borderColor: done ? '#10b981' : undefined, color: done ? '#10b981' : '#f59e0b' }}
    >
      {loading ? 'Provisioning...' : done ? '✓ Trial Set' : '🎁 Set Trial (50 credits)'}
    </Button>
  );
};

const ConfirmPaymentButton = ({ customerId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      await adminApiCall(`/admin/customers/${customerId}/confirm-payment`, {
        method: 'POST',
        body: JSON.stringify({ notes }),
      });
      setOpen(false);
      await onSuccess();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 13, boxSizing: 'border-box' };

  return (
    <>
      <Button variant="primary" icon={CheckCircle} onClick={() => setOpen(true)} style={{ background: '#10b981', borderColor: '#10b981' }}>
        Confirm Payment Received
      </Button>
      {open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <Card style={{ padding: 28, width: 420 }}>
            <h3 style={{ margin: '0 0 8px', color: '#f8fafc', fontSize: 18 }}>Confirm Payment Received</h3>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748b' }}>
              This will restore the account to active, reset credits for the new billing period, and advance the renewal date.
            </p>
            {error && <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', color: '#fca5a5', fontSize: 12, marginBottom: 12 }}>{error}</div>}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Internal Notes (optional)</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Invoice #1042 paid via wire transfer" rows={3}
                style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <Button variant="secondary" onClick={() => setOpen(false)} style={{ flex: 1 }}>Cancel</Button>
              <Button variant="primary" onClick={handleConfirm} disabled={loading} style={{ flex: 1, background: '#10b981' }}>
                {loading ? 'Confirming...' : 'Confirm & Restore'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
};

const CustomersList = ({ onSelectCustomer }) => {
  const [customers, setCustomers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [showProvision, setShowProvision] = useState(false);

  useEffect(() => {
    loadCustomers();
  }, [pagination.page, statusFilter, tierFilter]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: 25,
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
        ...(tierFilter && { tier: tierFilter }),
      });
      const result = await adminApiCall(`/admin/customers?${params}`);
      setCustomers(result.customers);
      setPagination(result.pagination);
    } catch (err) {
      console.error('Load customers error:', err);
    }
    setLoading(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(p => ({ ...p, page: 1 }));
    loadCustomers();
  };

  return (
    <div>
      {showProvision && (
        <ProvisionCustomerModal
          onClose={() => setShowProvision(false)}
          onSuccess={() => { setShowProvision(false); loadCustomers(); }}
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: '0 0 4px', fontSize: 28, fontWeight: 800, color: '#f8fafc' }}>Customers</h1>
          <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>{pagination.total} total customers</p>
        </div>
        <Button variant="primary" icon={Plus} onClick={() => setShowProvision(true)}>Provision Customer</Button>
      </div>

      <Card style={{ marginBottom: 24 }}>
        <div style={{ padding: 16, display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <form onSubmit={handleSearch} style={{ flex: 1 }}>
            <Input
              placeholder="Search by name or email..."
              value={search}
              onChange={setSearch}
              icon={Search}
              style={{ marginBottom: 0 }}
            />
          </form>
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
            style={{ padding: '12px 16px', borderRadius: 10, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 14 }}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="trial">Trial</option>
            <option value="cancelled">Cancelled</option>
            <option value="past_due">Past Due</option>
          </select>
          <select
            value={tierFilter}
            onChange={e => { setTierFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
            style={{ padding: '12px 16px', borderRadius: 10, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 14 }}
          >
            <option value="">All Tiers</option>
            <option value="trial">Trial</option>
            <option value="starter">Starter</option>
            <option value="professional">Professional</option>
            <option value="enterprise">Enterprise</option>
          </select>
          <Button variant="secondary" icon={RefreshCw} onClick={loadCustomers}>Refresh</Button>
        </div>
      </Card>

      <Card>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #334155' }}>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Customer</th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Plan</th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Status</th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Users</th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Credits</th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>MRR</th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Last Activity</th>
                  <th style={{ padding: '16px 20px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map(customer => (
                  <tr key={customer.id} style={{ borderBottom: '1px solid #334155', cursor: 'pointer' }} onClick={() => onSelectCustomer(customer.id)}>
                    <td style={{ padding: '16px 20px' }}>
                      <div style={{ fontWeight: 600, color: '#f8fafc' }}>{customer.name}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>Since {new Date(customer.createdAt).toLocaleDateString()}</div>
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <Badge variant={customer.subscriptionTier === 'enterprise' ? 'success' : customer.subscriptionTier === 'trial' ? 'warning' : 'info'}>
                        {customer.subscriptionTier || 'Unknown'}
                      </Badge>
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <Badge variant={
                        customer.subscriptionStatus === 'active' ? 'success' :
                        customer.subscriptionStatus === 'past_due' ? 'danger' :
                        customer.subscriptionStatus === 'suspended' ? 'danger' :
                        customer.subscriptionStatus === 'trial' ? 'warning' : 'danger'
                      }>
                        {customer.subscriptionStatus === 'past_due' ? '⚠ Past Due' :
                         customer.subscriptionStatus === 'suspended' ? '🚨 Suspended' :
                         customer.subscriptionStatus || 'Unknown'}
                      </Badge>
                    </td>
                    <td style={{ padding: '16px 20px', color: '#f8fafc' }}>{customer.active_users}</td>
                    <td style={{ padding: '16px 20px', color: '#f8fafc' }}>{customer.creditsRemaining?.toLocaleString() || 0}</td>
                    <td style={{ padding: '16px 20px', color: '#10b981', fontWeight: 600 }}>${((customer.mrr || 0) / 100).toFixed(0)}</td>
                    <td style={{ padding: '16px 20px', color: '#64748b', fontSize: 13 }}>
                      {customer.last_login ? new Date(customer.last_login).toLocaleDateString() : 'Never'}
                    </td>
                    <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                      <Button variant="ghost" size="sm" icon={Eye} onClick={(e) => { e.stopPropagation(); onSelectCustomer(customer.id); }}>View</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #334155' }}>
              <span style={{ color: '#64748b', fontSize: 14 }}>
                Showing {((pagination.page - 1) * 25) + 1} - {Math.min(pagination.page * 25, pagination.total)} of {pagination.total}
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={ChevronLeft}
                  disabled={pagination.page === 1}
                  onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                >
                  Next
                  <ChevronRight size={14} />
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

// =============================================================================
// HUBSPOT COMPANY ID EDITOR — always visible in the HubSpot tab, allows setting or changing the ID
// =============================================================================

const HubSpotIdEditor = ({ customerId, currentId, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(currentId || '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  // Keep local value in sync if parent reloads
  React.useEffect(() => { if (!editing) setValue(currentId || ''); }, [currentId, editing]);

  const handleSave = async () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setSaving(true); setMsg(null);
    try {
      await adminApiCall(`/admin/customers/${customerId}/relink-hubspot`, {
        method: 'POST',
        body: JSON.stringify({ hubspotCompanyId: trimmed }),
      });
      setMsg({ type: 'success', text: `Linked to HubSpot company ${trimmed}` });
      setEditing(false);
      setTimeout(() => { setMsg(null); onSaved(); }, 1200);
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
    setSaving(false);
  };

  return (
    <div style={{ padding: '12px 16px', borderRadius: 10, background: '#0f172a', border: '1px solid #334155', marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
            HubSpot Company ID
          </div>
          {editing ? (
            <input
              autoFocus
              value={value}
              onChange={e => setValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
              placeholder="Paste HubSpot Company ID (e.g. 12345678)"
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #4f46e5', background: '#1e293b', color: '#f8fafc', fontSize: 14, fontFamily: 'monospace', boxSizing: 'border-box' }}
            />
          ) : (
            <div style={{ fontSize: 14, color: currentId ? '#818cf8' : '#475569', fontFamily: currentId ? 'monospace' : 'inherit', fontStyle: currentId ? 'normal' : 'italic' }}>
              {currentId || 'Not linked — paste a HubSpot Company ID to connect this org'}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {editing ? (
            <>
              <Button variant="secondary" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
              <Button variant="primary" onClick={handleSave} disabled={saving || !value.trim()}
                style={{ background: 'linear-gradient(135deg, #4f46e5, #6366f1)' }}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : (
            <Button variant="secondary" icon={Edit3} onClick={() => { setValue(currentId || ''); setEditing(true); }}>
              {currentId ? 'Change' : 'Set ID'}
            </Button>
          )}
        </div>
      </div>
      {msg && (
        <div style={{ marginTop: 8, fontSize: 12, color: msg.type === 'success' ? '#22c55e' : '#ef4444' }}>
          {msg.text}
        </div>
      )}
    </div>
  );
};

// LINK HUBSPOT PANEL — shown when an org has no hubspot_company_id
// =============================================================================

const LinkHubSpotPanel = ({ customerId, onLinked }) => {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleLink = async () => {
    if (!selectedCompany) return;
    setLinking(true); setError(null);
    try {
      const d = await adminApiCall(`/admin/customers/${customerId}/link-hubspot`, {
        method: 'POST',
        body: JSON.stringify({ hubspotCompanyId: selectedCompany.id }),
      });
      setSuccess(`Linked to "${selectedCompany.name}" — ${d.dealCount} deal(s) pulled from HubSpot.`);
      setTimeout(() => onLinked(), 1200);
    } catch (err) {
      setError(err.message);
    }
    setLinking(false);
  };

  return (
    <Card style={{ padding: 24, maxWidth: 560 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <AlertTriangle size={20} style={{ color: '#f59e0b' }} />
        </div>
        <div>
          <h4 style={{ margin: 0, color: '#f8fafc', fontWeight: 600 }}>Not Linked to HubSpot</h4>
          <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>Search for this customer's HubSpot company and link it to pull deal data.</p>
        </div>
      </div>

      {success && <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', color: '#6ee7b7', fontSize: 13, marginBottom: 12 }}>{success}</div>}
      {error && <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Search HubSpot Companies</label>
        <HubSpotCompanySearch onSelect={c => setSelectedCompany(c)} placeholder="Type company name to search..." />
      </div>

      {selectedCompany && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 8, background: 'rgba(10,102,194,0.1)', border: '1px solid rgba(10,102,194,0.3)', marginBottom: 12 }}>
          <div>
            <div style={{ fontWeight: 600, color: '#f8fafc', fontSize: 14 }}>{selectedCompany.name}</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>HubSpot ID: {selectedCompany.id}{selectedCompany.domain ? ` · ${selectedCompany.domain}` : ''}</div>
            {selectedCompany.alreadyLinked && <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 2 }}>Already linked to another kNine org</div>}
          </div>
          <Button variant="primary" onClick={handleLink} disabled={linking} style={{ flexShrink: 0 }}>
            {linking ? 'Linking...' : 'Link & Pull Deals'}
          </Button>
        </div>
      )}

      <p style={{ margin: 0, fontSize: 12, color: '#475569' }}>
        This will set <code style={{ color: '#94a3b8' }}>knine_org_id</code> on the HubSpot company and import all associated deal data into kNine.
      </p>
    </Card>
  );
};

// =============================================================================
// RESET PASSWORD PANEL
// =============================================================================

const ResetPasswordPanel = ({ customerId, user, onDone, onCancel }) => {
  const { token } = useAdmin();
  const [newPassword, setNewPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    setSaving(true); setErr(null);
    try {
      await adminApiCall(`/admin/customers/${customerId}/users/${user.id}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ mode: 'set', password: newPassword })
      });
      setDone(true);
      setTimeout(() => onDone(), 1500);
    } catch (e) {
      setErr(e.message || 'Failed to set password');
    }
    setSaving(false);
  };

  return (
    <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 16, marginTop: 4 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#94a3b8', marginBottom: 10 }}>
        Set password for <span style={{ color: '#f8fafc' }}>{user.name || user.email}</span>
      </div>
      {done ? (
        <div style={{ fontSize: 13, color: '#4ade80' }}>Password updated. They can change it anytime in their account settings.</div>
      ) : (
        <>
          <div style={{ position: 'relative', marginBottom: 6 }}>
            <input
              autoFocus
              type={showPw ? 'text' : 'password'}
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && newPassword.length >= 8 && submit()}
              placeholder="New password (min 8 characters)"
              style={{ width: '100%', background: '#1e293b', border: '1px solid #334155', borderRadius: 6, padding: '8px 48px 8px 12px', color: '#f8fafc', fontSize: 13, boxSizing: 'border-box' }}
            />
            <button
              onClick={() => setShowPw(v => !v)}
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 11 }}
            >
              {showPw ? 'hide' : 'show'}
            </button>
          </div>
          {err && <div style={{ fontSize: 12, color: '#f87171', marginBottom: 8 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button
              onClick={submit}
              disabled={saving || newPassword.length < 8}
              style={{ fontSize: 12, padding: '6px 16px', borderRadius: 6, border: 'none', background: !saving && newPassword.length >= 8 ? '#6366f1' : '#334155', color: '#fff', cursor: !saving && newPassword.length >= 8 ? 'pointer' : 'default' }}
            >
              {saving ? 'Saving...' : 'Set Password'}
            </button>
            <button onClick={onCancel} style={{ fontSize: 12, padding: '6px 14px', borderRadius: 6, border: '1px solid #334155', background: 'none', color: '#94a3b8', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// =============================================================================
// MOVE USER TO ORG PANEL
// =============================================================================

const MoveUserToOrgPanel = ({ customerId, userId, userName, onDone, onCancel }) => {
  const { token } = useAdmin();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const doSearch = async (q) => {
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const r = await fetch(`/api/admin/customers?search=${encodeURIComponent(q)}&limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      setResults((d.customers || []).filter(o => o.id !== customerId));
    } catch (_) {}
    setSearching(false);
  };

  useEffect(() => {
    const t = setTimeout(() => doSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const move = async () => {
    if (!selected) return;
    setSaving(true); setMsg(null);
    try {
      const r = await fetch(`/api/admin/customers/${customerId}/users/${userId}/move-to-org`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetOrgId: selected.id })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setMsg({ type: 'success', text: d.message || 'User moved successfully' });
      setTimeout(() => onDone(), 1200);
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
      setSaving(false);
    }
  };

  return (
    <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 16, marginTop: 4 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#94a3b8', marginBottom: 10 }}>
        Move <span style={{ color: '#f8fafc' }}>{userName}</span> to a different organization
      </div>
      <input
        autoFocus
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search org by name..."
        style={{ width: '100%', background: '#1e293b', border: '1px solid #334155', borderRadius: 6, padding: '8px 12px', color: '#f8fafc', fontSize: 13, boxSizing: 'border-box', marginBottom: 8 }}
      />
      {searching && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>Searching...</div>}
      {results.length > 0 && (
        <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #334155', borderRadius: 6, marginBottom: 10 }}>
          {results.map(org => (
            <div
              key={org.id}
              onClick={() => setSelected(org)}
              style={{
                padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                background: selected?.id === org.id ? '#1e3a5f' : 'transparent',
                borderBottom: '1px solid #1e293b',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}
            >
              <span style={{ color: '#f8fafc', fontWeight: selected?.id === org.id ? 600 : 400 }}>{org.name}</span>
              <span style={{ fontSize: 11, color: '#64748b' }}>{org.subscriptionStatus} · {org.subscriptionTier}</span>
            </div>
          ))}
        </div>
      )}
      {selected && (
        <div style={{ fontSize: 13, color: '#a3e635', marginBottom: 10 }}>
          Selected: <strong>{selected.name}</strong>
        </div>
      )}
      {msg && (
        <div style={{ fontSize: 13, color: msg.type === 'success' ? '#4ade80' : '#f87171', marginBottom: 8 }}>{msg.text}</div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={move}
          disabled={!selected || saving}
          style={{ fontSize: 12, padding: '6px 16px', borderRadius: 6, border: 'none', background: selected && !saving ? '#6366f1' : '#334155', color: '#fff', cursor: selected && !saving ? 'pointer' : 'default' }}
        >
          {saving ? 'Moving...' : 'Confirm Move'}
        </button>
        <button
          onClick={onCancel}
          style={{ fontSize: 12, padding: '6px 14px', borderRadius: 6, border: '1px solid #334155', background: 'none', color: '#94a3b8', cursor: 'pointer' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// EDIT USER PANEL (credit allocation + role + status)
// =============================================================================

const EditUserPanel = ({ customerId, user, orgCredits, onDone, onCancel }) => {
  const { token } = useAdmin();
  const [name, setName] = useState(user.name || '');
  const [email, setEmail] = useState(user.email || '');
  const [allocationType, setAllocationType] = useState(user.creditAllocationType || 'none');
  const [allocationValue, setAllocationValue] = useState(user.creditAllocationValue ?? '');
  const [role, setRole] = useState(user.role || 'user');
  const [isActive, setIsActive] = useState(user.isActive !== false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      // Update role/active status/name/email
      const r1 = await fetch(`/api/admin/customers/${customerId}/team/${user.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ role, isActive, name: name.trim(), email: email.trim() })
      });
      if (!r1.ok) { let d = {}; try { d = await r1.json(); } catch (_) {} throw new Error(d.error || `HTTP ${r1.status}`); }

      // Update credit allocation
      const r2 = await fetch(`/api/admin/customers/${customerId}/user-limits`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          allocationType: allocationType || 'none',
          allocationValue: allocationType !== 'none' ? Number(allocationValue) : null,
          reason: 'Admin override via dashboard'
        })
      });
      if (!r2.ok) { let d = {}; try { d = await r2.json(); } catch (_) {} throw new Error(d.error || `HTTP ${r2.status}`); }

      setMsg({ type: 'success', text: 'Saved' });
      setTimeout(() => onDone(), 900);
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
      setSaving(false);
    }
  };

  return (
    <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 16, marginTop: 4 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#94a3b8', marginBottom: 14 }}>
        Edit <span style={{ color: '#f8fafc' }}>{user.name || user.email}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Name */}
        <div>
          <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>NAME</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Full name"
            style={{ width: '100%', background: '#1e293b', border: '1px solid #334155', borderRadius: 6, padding: '7px 10px', color: '#f8fafc', fontSize: 13, boxSizing: 'border-box' }}
          />
        </div>
        {/* Email */}
        <div>
          <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>EMAIL</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="user@company.com"
            style={{ width: '100%', background: '#1e293b', border: '1px solid #334155', borderRadius: 6, padding: '7px 10px', color: '#f8fafc', fontSize: 13, boxSizing: 'border-box' }}
          />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Role */}
        <div>
          <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>ROLE</label>
          <select
            value={role}
            onChange={e => setRole(e.target.value)}
            style={{ width: '100%', background: '#1e293b', border: '1px solid #334155', borderRadius: 6, padding: '7px 10px', color: '#f8fafc', fontSize: 13 }}
          >
            <option value="user">User</option>
            <option value="admin">Admin</option>
            <option value="owner">Owner</option>
            <option value="viewer">Viewer</option>
          </select>
        </div>
        {/* Status */}
        <div>
          <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>STATUS</label>
          <select
            value={isActive ? 'active' : 'inactive'}
            onChange={e => setIsActive(e.target.value === 'active')}
            style={{ width: '100%', background: '#1e293b', border: '1px solid #334155', borderRadius: 6, padding: '7px 10px', color: '#f8fafc', fontSize: 13 }}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        {/* Credit allocation type */}
        <div>
          <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>CREDIT LIMIT TYPE</label>
          <select
            value={allocationType || 'none'}
            onChange={e => setAllocationType(e.target.value)}
            style={{ width: '100%', background: '#1e293b', border: '1px solid #334155', borderRadius: 6, padding: '7px 10px', color: '#f8fafc', fontSize: 13 }}
          >
            <option value="none">No limit (unlimited)</option>
            <option value="fixed">Fixed credits</option>
            <option value="percentage">% of org allowance</option>
          </select>
        </div>
        {/* Credit value */}
        {allocationType && allocationType !== 'none' && (
          <div>
            <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>
              {allocationType === 'fixed' ? 'CREDITS PER PERIOD' : 'PERCENTAGE (%)'}
              {orgCredits != null && allocationType === 'fixed' && (
                <span style={{ fontWeight: 400, color: '#475569', marginLeft: 8 }}>org pool: {orgCredits.toLocaleString()}</span>
              )}
            </label>
            <input
              type="number"
              min="0"
              value={allocationValue}
              onChange={e => setAllocationValue(e.target.value)}
              placeholder={allocationType === 'fixed' ? 'e.g. 500' : 'e.g. 25'}
              style={{ width: '100%', background: '#1e293b', border: '1px solid #334155', borderRadius: 6, padding: '7px 10px', color: '#f8fafc', fontSize: 13, boxSizing: 'border-box' }}
            />
          </div>
        )}
      </div>
      {msg && (
        <div style={{ fontSize: 13, color: msg.type === 'success' ? '#4ade80' : '#f87171', marginTop: 10 }}>{msg.text}</div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button
          onClick={save}
          disabled={saving}
          style={{ fontSize: 12, padding: '6px 16px', borderRadius: 6, border: 'none', background: saving ? '#334155' : '#6366f1', color: '#fff', cursor: saving ? 'default' : 'pointer' }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
        <button
          onClick={onCancel}
          style={{ fontSize: 12, padding: '6px 14px', borderRadius: 6, border: '1px solid #334155', background: 'none', color: '#94a3b8', cursor: 'pointer' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// CUSTOMER DETAIL
// =============================================================================

const CustomerDetail = ({ customerId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [creditModal, setCreditModal] = useState(false);
  const [noteModal, setNoteModal] = useState(false);
  const [impersonateModal, setImpersonateModal] = useState(false);
  const [impersonateDefaultUser, setImpersonateDefaultUser] = useState('');
  const [subscriptionModal, setSubscriptionModal] = useState(false);
  const [seatModal, setSeatModal] = useState(false);
  const [features, setFeatures] = useState(null);
  const [searches, setSearches] = useState(null);
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [expandedSearch, setExpandedSearch] = useState(null);
  const [platforms, setPlatforms] = useState(null);
  const [platformsLoading, setPlatformsLoading] = useState(false);
  const [modules, setModules] = useState(null);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [loginActivity, setLoginActivity] = useState(null);
  const [loginActivityLoading, setLoginActivityLoading] = useState(false);
  const [creditUsage, setCreditUsage] = useState(null);
  const [featureError, setFeatureError] = useState(null);
  const [searchConfig, setSearchConfig] = useState(null);
  const [searchConfigLoading, setSearchConfigLoading] = useState(false);
  const [searchConfigError, setSearchConfigError] = useState(null);
  const [searchConfigSaving, setSearchConfigSaving] = useState(false);
  const [hubspotData, setHubspotData] = useState(null);
  const [hubspotLoading, setHubspotLoading] = useState(false);
  const [enrichments, setEnrichments] = useState(null);
  const [enrichmentsLoading, setEnrichmentsLoading] = useState(false);
  const [enrichmentPage, setEnrichmentPage] = useState(1);
  const [addUserModal, setAddUserModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [mergeModal, setMergeModal] = useState(false);
  const [movingUserId, setMovingUserId] = useState(null);
  const [editingUserId, setEditingUserId] = useState(null);
  const [resetPwUserId, setResetPwUserId] = useState(null);

  useEffect(() => {
    loadCustomer();
  }, [customerId]);

  const loadCustomer = async () => {
    setLoading(true);
    setError(null);
    setData({}); // clear stale data immediately so previous org's users/info don't show
    try {
      const result = await adminApiCall(`/admin/customers/${customerId}`);
      setData(result);
    } catch (err) {
      console.error('Load customer error:', err);
      setError(err.message || 'Failed to load customer');
    }
    setLoading(false);
  };

  const loadFeatures = async () => {
    try {
      const result = await adminApiCall(`/admin/customers/${customerId}/features`);
      setFeatures(result);
    } catch (err) {
      console.error('Load features error:', err);
    }
  };

  const loadSearches = async () => {
    try {
      const result = await adminApiCall(`/admin/customers/${customerId}/searches`);
      setSearches(result);
    } catch (err) {
      console.error('Load searches error:', err);
    }
  };

  const loadActivity = async () => {
    setActivityLoading(true);
    try {
      const result = await adminApiCall(`/admin/customers/${customerId}/activity?limit=200`);
      setActivity(result);
    } catch (err) {
      console.error('Load activity error:', err);
      setActivity({ activities: [] });
    }
    setActivityLoading(false);
  };

  const loadCreditUsage = async () => {
    try {
      const result = await adminApiCall(`/admin/customers/${customerId}/credit-usage`);
      setCreditUsage(result);
    } catch (err) {
      console.error('Load credit usage error:', err);
    }
  };

  const loadSearchConfig = async () => {
    setSearchConfigLoading(true);
    setSearchConfigError(null);
    try {
      const result = await adminApiCall(`/admin/customers/${customerId}/search-config`);
      setSearchConfig(result);
    } catch (err) {
      console.error('Load search config error:', err);
      setSearchConfigError(err.message || 'Failed to load search config');
    }
    setSearchConfigLoading(false);
  };

  const saveSearchConfig = async (updates) => {
    setSearchConfigSaving(true);
    try {
      await adminApiCall(`/admin/customers/${customerId}/search-config`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      await loadSearchConfig();
    } catch (err) {
      console.error('Save search config error:', err);
      setSearchConfigError(err.message || 'Failed to save');
    }
    setSearchConfigSaving(false);
  };

  const removeUserSearchOverride = async (userId) => {
    try {
      await adminApiCall(`/admin/customers/${customerId}/search-config/users/${userId}`, { method: 'DELETE' });
      await loadSearchConfig();
    } catch (err) {
      console.error('Remove user override error:', err);
    }
  };

  const loadHubspot = async (forceRefresh = false) => {
    setHubspotLoading(true);
    try {
      const result = await adminApiCall(`/admin/customers/${customerId}/hubspot${forceRefresh ? '?refresh=true' : ''}`);
      setHubspotData(result);
    } catch (err) {
      console.error('Load HubSpot error:', err);
      setHubspotData({ deals: [], error: err.message });
    }
    setHubspotLoading(false);
  };

  const loadPlatforms = async () => {
    setPlatformsLoading(true);
    try {
      const result = await adminApiCall(`/admin/customers/${customerId}/platforms`);
      setPlatforms(result);
    } catch (err) {
      console.error('Load platforms error:', err);
      setPlatforms({ error: err.message });
    }
    setPlatformsLoading(false);
  };

  const togglePlatform = async (platform, enabled) => {
    try {
      await adminApiCall(`/admin/customers/${customerId}/platforms`, {
        method: 'POST',
        body: JSON.stringify({ platform, enabled, reason: `${enabled ? 'Enabled' : 'Disabled'} by admin` }),
      });
      await loadPlatforms();
    } catch (err) {
      alert(`Failed to toggle platform: ${err.message}`);
    }
  };

  const loadModules = async () => {
    setModulesLoading(true);
    try {
      const result = await adminApiCall(`/admin/customers/${customerId}/modules`);
      setModules(result);
    } catch (err) {
      console.error('Load modules error:', err);
      setModules({ error: err.message });
    }
    setModulesLoading(false);
  };

  const toggleModule = async (moduleKey, enabled) => {
    try {
      await adminApiCall(`/admin/customers/${customerId}/modules`, {
        method: 'POST',
        body: JSON.stringify({ moduleKey, enabled, reason: `${enabled ? 'Enabled' : 'Disabled'} by admin` }),
      });
      await loadModules();
    } catch (err) {
      alert(`Failed to toggle module: ${err.message}`);
    }
  };

  const loadLoginActivity = async () => {
    setLoginActivityLoading(true);
    try {
      const result = await adminApiCall(`/admin/customers/${customerId}/login-activity?limit=100`);
      setLoginActivity(result);
    } catch (err) {
      console.error('Load login activity error:', err);
      setLoginActivity({ logins: [], stats: {}, userSummary: [] });
    }
    setLoginActivityLoading(false);
  };

  const loadEnrichments = async (page = 1, userId) => {
    setEnrichmentsLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (userId) params.set('userId', userId);
      const result = await adminApiCall(`/admin/customers/${customerId}/enrichments?${params}`);
      setEnrichments(prev => ({
        ...result,
        // keep userBreakdown from first load so the filter dropdown doesn't disappear
        userBreakdown: userId ? (prev?.userBreakdown || result.userBreakdown) : result.userBreakdown,
      }));
      setEnrichmentPage(page);
    } catch (err) {
      console.error('Load enrichments error:', err);
      setEnrichments({ enrichments: [], userBreakdown: [], pagination: { total: 0 } });
    }
    setEnrichmentsLoading(false);
  };

  useEffect(() => {
    if (activeTab === 'features' && !features) loadFeatures();
    if (activeTab === 'searches' && !searches) loadSearches();
    if (activeTab === 'activity' && !activity) loadActivity();
    if (activeTab === 'credits' && !creditUsage) loadCreditUsage();
    if (activeTab === 'searchConfig' && !searchConfig) loadSearchConfig();
    if (activeTab === 'hubspot' && !hubspotData) loadHubspot();
    if (activeTab === 'enrichments' && !enrichments) loadEnrichments();
    if (activeTab === 'platforms' && !platforms) loadPlatforms();
    if (activeTab === 'modules' && !modules) loadModules();
    if (activeTab === 'loginActivity' && !loginActivity) loadLoginActivity();
  }, [activeTab]);

  if (loading) return <LoadingSpinner text="Loading customer details..." />;
  if (error) return <div style={{ color: '#ef4444', padding: 40, textAlign: 'center' }}>Error: {error}</div>;
  if (!data) return <div style={{ color: '#ef4444', padding: 40, textAlign: 'center' }}>Customer not found</div>;

  const { organization, users = [], integrations = [], credits = {}, seats = {}, addons = {}, notes = [], loginHistory = [], creditTransactions = [] } = data;
  const plan = organization?.subscriptionPlan || organization?.subscriptionTier || 'free';

  const tabs = [
    { id: 'overview',       label: 'Overview',      icon: Building2 },
    { id: 'users',          label: 'Users',          icon: Users },
    { id: 'loginActivity',  label: 'Logins',         icon: Key },
    { id: 'enrichments',    label: 'Enrichments',    icon: History },
    { id: 'platforms',      label: 'Platforms',      icon: Globe },
    { id: 'modules',        label: 'Modules',        icon: Settings },
    { id: 'features',       label: 'Flags',          icon: Zap },
    { id: 'searchConfig',   label: 'Search',         icon: Filter },
    { id: 'hubspot',        label: 'HubSpot',        icon: FileText },
    { id: 'credits',        label: 'Credits',        icon: CreditCard },
    { id: 'searches',       label: 'History',        icon: Search },
    { id: 'activity',       label: 'Activity',       icon: Activity },
    { id: 'notes',          label: 'Notes',          icon: MessageSquare },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Button variant="ghost" icon={ChevronLeft} onClick={onBack} style={{ marginBottom: 16 }}>Back to Customers</Button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ margin: '0 0 8px', fontSize: 28, fontWeight: 800, color: '#f8fafc' }}>{organization.name}</h1>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <Badge variant={organization.subscriptionTier === 'enterprise' ? 'success' : organization.subscriptionTier === 'trial' ? 'warning' : 'info'}>
                {organization.subscriptionTier}
              </Badge>
              <Badge variant={
                organization.subscriptionStatus === 'active' ? 'success' :
                organization.subscriptionStatus === 'past_due' ? 'danger' :
                organization.subscriptionStatus === 'suspended' ? 'danger' : 'warning'
              }>
                {organization.subscriptionStatus === 'past_due' ? '⚠ Past Due' :
                 organization.subscriptionStatus === 'suspended' ? '🚨 Suspended' :
                 organization.subscriptionStatus}
              </Badge>
              {organization.billing_interval && (
                <Badge variant="info">{organization.billing_interval === 'monthly' ? 'Monthly billing' : 'Annual billing'}</Badge>
              )}
              <span style={{ color: '#64748b', fontSize: 14 }}>Customer since {new Date(organization.createdAt).toLocaleDateString()}</span>
            </div>
            {['past_due', 'suspended'].includes(organization.subscriptionStatus) && (
              <div style={{ marginTop: 10, padding: '8px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', fontSize: 13, color: '#fca5a5' }}>
                ⚠ Payment overdue
                {organization.billing_past_due_at && ` since ${new Date(organization.billing_past_due_at).toLocaleDateString()}`}.
                Confirm payment received below to restore access.
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12, flexDirection: 'column', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', gap: 12 }}>
              <Button variant="secondary" icon={Plus} onClick={() => setCreditModal(true)}>Add Credits</Button>
              <Button variant="secondary" icon={MessageSquare} onClick={() => setNoteModal(true)}>Add Note</Button>
              <Button variant="secondary" icon={ArrowUpRight} onClick={() => setMergeModal(true)}>Merge Into Org</Button>
              <Button variant="primary" icon={User} onClick={() => setImpersonateModal(true)}>Login as User</Button>
              <Button variant="danger" icon={Trash2} onClick={() => setDeleteModal(true)}>Delete</Button>
            </div>
            {(plan === 'free' || !organization.subscriptionTier || organization.subscriptionTier === 'trial') && (
              <ProvisionTrialButton customerId={customerId} onSuccess={loadCustomer} />
            )}
            {['past_due', 'suspended'].includes(organization.subscriptionStatus) && (
              <ConfirmPaymentButton customerId={customerId} onSuccess={loadCustomer} />
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', marginBottom: 24, borderBottom: '1px solid #334155', overflowX: 'auto', scrollbarWidth: 'none' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '10px 14px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid #0a66c2' : '2px solid transparent',
              color: activeTab === tab.id ? '#0a66c2' : '#64748b',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              marginBottom: -1,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            <tab.icon size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
          <div>
            <Card style={{ padding: 24, marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Subscription & Billing</h3>
                <Button variant="secondary" icon={Edit3} onClick={() => setSubscriptionModal(true)}>Change Plan</Button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
                <div style={{ padding: 16, background: '#0f172a', borderRadius: 10 }}>
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>Current Plan</div>
                  <div style={{ color: '#f8fafc', fontWeight: 700, fontSize: 18, textTransform: 'capitalize' }}>{plan}</div>
                </div>
                <div style={{ padding: 16, background: '#0f172a', borderRadius: 10 }}>
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>User Limit</div>
                  <div style={{ color: '#f8fafc', fontWeight: 700, fontSize: 18 }}>
                    {seats.effective === -1 ? 'Unlimited' : `${users.filter(u => u.isActive).length} / ${seats.effective}`}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    Base: {seats.baseLimit === -1 ? '∞' : seats.baseLimit} + {seats.additional || 0} additional
                  </div>
                </div>
                <div style={{ padding: 16, background: '#0f172a', borderRadius: 10 }}>
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>Renewal Date</div>
                  <div style={{ color: '#f8fafc', fontWeight: 700, fontSize: 18 }}>
                    {credits.renewalDate ? new Date(credits.renewalDate).toLocaleDateString() : 'N/A'}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                <Button variant="secondary" icon={Users} onClick={() => setSeatModal(true)}>Manage Seats</Button>
                <Button variant="secondary" icon={Zap} onClick={() => setCreditModal(true)}>Add Credits</Button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>Tracker Add-on</div>
                  <Badge variant={addons.hasTracker ? 'success' : 'default'}>{addons.hasTracker ? 'Active' : 'Not Active'}</Badge>
                </div>
                <div>
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>Fee Recovery Add-on</div>
                  <Badge variant={addons.hasFeeRecovery ? 'success' : 'default'}>{addons.hasFeeRecovery ? 'Active' : 'Not Active'}</Badge>
                </div>
              </div>
            </Card>

            <Card style={{ padding: 24, marginBottom: 24 }}>
              <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Credits Summary</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <div style={{ padding: 16, background: '#0f172a', borderRadius: 10, textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#10b981' }}>{credits.total?.toLocaleString() || 0}</div>
                  <div style={{ fontSize: 13, color: '#64748b' }}>Total Credits</div>
                </div>
                <div style={{ padding: 16, background: '#0f172a', borderRadius: 10, textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#0a66c2' }}>{credits.subscription?.toLocaleString() || 0}</div>
                  <div style={{ fontSize: 13, color: '#64748b' }}>Subscription</div>
                </div>
                <div style={{ padding: 16, background: '#0f172a', borderRadius: 10, textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#f59e0b' }}>{credits.purchased?.toLocaleString() || 0}</div>
                  <div style={{ fontSize: 13, color: '#64748b' }}>Purchased</div>
                </div>
              </div>
            </Card>

            <Card style={{ padding: 24 }}>
              <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Integrations</h3>
              {integrations.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {integrations.map(int => (
                    <div key={int.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 12, background: '#0f172a', borderRadius: 10 }}>
                      <div>
                        <div style={{ fontWeight: 600, color: '#f8fafc', textTransform: 'capitalize' }}>{int.integration_type || int.system}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                          Last sync: {int.last_sync_at ? new Date(int.last_sync_at).toLocaleString() : 'Never'}
                        </div>
                      </div>
                      <Badge variant={int.is_active || int.isActive ? 'success' : 'danger'}>{int.is_active || int.isActive ? 'Active' : 'Inactive'}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: '#64748b', textAlign: 'center', padding: 20 }}>No integrations configured</div>
              )}
            </Card>
          </div>

          <div>
            <Card style={{ padding: 24, marginBottom: 24 }}>
              <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Account Details</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>Organization ID</div>
                  <div style={{ color: '#f8fafc', fontFamily: 'monospace', fontSize: 12 }}>{organization.id}</div>
                </div>
                <div>
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>Billing Email</div>
                  <div style={{ color: '#f8fafc' }}>{organization.billing_email || organization.billingEmail || 'Not set'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>Stripe Customer</div>
                  <div style={{ color: '#f8fafc', fontFamily: 'monospace', fontSize: 11 }}>{organization.stripe_customer_id || organization.stripeCustomerId || 'Not connected'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>Health Score</div>
                  <div style={{ color: organization.health_score > 70 ? '#10b981' : organization.health_score > 40 ? '#f59e0b' : '#ef4444', fontWeight: 600 }}>
                    {organization.health_score || 'N/A'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>Last Activity</div>
                  <div style={{ color: '#f8fafc' }}>{organization.last_activity_at ? new Date(organization.last_activity_at).toLocaleString() : 'N/A'}</div>
                </div>
              </div>
            </Card>

            <Card style={{ padding: 24 }}>
              <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Recent Notes</h3>
              {notes.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {notes.slice(0, 3).map(note => (
                    <div key={note.id} style={{ padding: 12, background: '#0f172a', borderRadius: 10 }}>
                      <div style={{ fontSize: 13, color: '#f8fafc', marginBottom: 8 }}>{note.note}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>
                        {note.admin_name} • {new Date(note.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: '#64748b', textAlign: 'center', padding: 20 }}>No notes yet</div>
              )}
            </Card>
          </div>
        </div>
      )}

      {addUserModal && (
        <AddUserModal
          orgId={customerId}
          orgName={organization.name}
          orgCredits={credits?.total ?? null}
          onClose={() => setAddUserModal(false)}
          onSuccess={() => { setAddUserModal(false); loadCustomer(); }}
        />
      )}

      {activeTab === 'users' && (
        <Card>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#f8fafc' }}>Team Members</h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
                {users.filter(u => u.isActive).length} of {seats.effective === -1 ? 'unlimited' : seats.effective} seats used
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Button variant="secondary" icon={Plus} onClick={() => setAddUserModal(true)}>Add User</Button>
              <Button variant="secondary" icon={Users} onClick={() => setSeatModal(true)}>Manage Seats</Button>
            </div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #334155' }}>
                <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>User</th>
                <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Role</th>
                <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Credit Usage</th>
                <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Credit Limit</th>
                <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Status</th>
                <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Last Active</th>
                <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <React.Fragment key={user.id}>
                  <tr style={{ borderBottom: (movingUserId === user.id || editingUserId === user.id || resetPwUserId === user.id) ? 'none' : '1px solid #334155' }}>
                    <td style={{ padding: '16px 20px' }}>
                      <div style={{ fontWeight: 600, color: '#f8fafc' }}>{user.name || 'No name'}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>{user.email}</div>
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <Badge variant={user.role === 'admin' || user.role === 'owner' ? 'info' : 'default'}>{user.role}</Badge>
                    </td>
                    <td style={{ padding: '16px 20px', color: '#f8fafc', fontWeight: 500 }}>
                      {user.creditUsageThisPeriod || 0}
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      {user.creditAllocationType === 'fixed' ? (
                        <span style={{ color: '#f8fafc' }}>{user.creditAllocationValue} credits</span>
                      ) : user.creditAllocationType === 'percentage' ? (
                        <span style={{ color: '#f8fafc' }}>{user.creditAllocationValue}%</span>
                      ) : (
                        <span style={{ color: '#64748b' }}>Unlimited</span>
                      )}
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <Badge variant={user.isActive ? 'success' : 'danger'}>{user.isActive ? 'Active' : 'Inactive'}</Badge>
                    </td>
                    <td style={{ padding: '16px 20px', color: '#64748b', fontSize: 13 }}>
                      {user.last_active_at ? new Date(user.last_active_at).toLocaleString() : 'Never'}
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button
                          onClick={() => { setEditingUserId(editingUserId === user.id ? null : user.id); setMovingUserId(null); setResetPwUserId(null); }}
                          style={{ fontSize: 12, color: '#34d399', background: 'none', border: `1px solid ${editingUserId === user.id ? '#34d399' : 'rgba(52,211,153,0.3)'}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => { setResetPwUserId(resetPwUserId === user.id ? null : user.id); setEditingUserId(null); setMovingUserId(null); }}
                          style={{ fontSize: 12, color: '#f59e0b', background: 'none', border: `1px solid ${resetPwUserId === user.id ? '#f59e0b' : 'rgba(245,158,11,0.3)'}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}
                        >
                          Password
                        </button>
                        <button
                          onClick={() => { setMovingUserId(movingUserId === user.id ? null : user.id); setEditingUserId(null); setResetPwUserId(null); }}
                          style={{ fontSize: 12, color: '#818cf8', background: 'none', border: `1px solid ${movingUserId === user.id ? '#818cf8' : 'rgba(129,140,248,0.3)'}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}
                        >
                          Move
                        </button>
                        <button
                          onClick={() => { setImpersonateDefaultUser(user.id); setImpersonateModal(true); }}
                          style={{ fontSize: 12, color: '#94a3b8', background: 'none', border: '1px solid rgba(148,163,184,0.25)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}
                          title="Open app as this user"
                        >
                          Open as
                        </button>
                      </div>
                    </td>
                  </tr>
                  {editingUserId === user.id && (
                    <tr style={{ borderBottom: '1px solid #334155' }}>
                      <td colSpan={7} style={{ padding: '0 20px 16px' }}>
                        <EditUserPanel
                          customerId={customerId}
                          user={user}
                          orgCredits={credits?.total ?? null}
                          onDone={() => { setEditingUserId(null); loadCustomer(); }}
                          onCancel={() => setEditingUserId(null)}
                        />
                      </td>
                    </tr>
                  )}
                  {resetPwUserId === user.id && (
                    <tr style={{ borderBottom: '1px solid #334155' }}>
                      <td colSpan={7} style={{ padding: '0 20px 16px' }}>
                        <ResetPasswordPanel
                          customerId={customerId}
                          user={user}
                          onDone={() => setResetPwUserId(null)}
                          onCancel={() => setResetPwUserId(null)}
                        />
                      </td>
                    </tr>
                  )}
                  {movingUserId === user.id && (
                    <tr style={{ borderBottom: '1px solid #334155' }}>
                      <td colSpan={7} style={{ padding: '0 20px 16px' }}>
                        <MoveUserToOrgPanel
                          customerId={customerId}
                          userId={user.id}
                          userName={user.name || user.email}
                          onDone={() => { setMovingUserId(null); loadCustomer(); }}
                          onCancel={() => setMovingUserId(null)}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {activeTab === 'enrichments' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Enrichment History</h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Every record enriched by this org — name, LinkedIn, credits charged, and which user ran it</p>
            </div>
            <Button variant="secondary" icon={RefreshCw} onClick={() => loadEnrichments(enrichmentPage)} disabled={enrichmentsLoading}>Refresh</Button>
          </div>

          {enrichmentsLoading && !enrichments ? <LoadingSpinner text="Loading enrichments..." /> : enrichments ? (
            <>
              {/* Per-user summary */}
              {enrichments.userBreakdown?.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginBottom: 20 }}>
                  {enrichments.userBreakdown.map(row => (
                    <Card key={row.userId} style={{ padding: 16 }}>
                      <div style={{ fontWeight: 600, color: '#f8fafc', fontSize: 14, marginBottom: 2 }}>{row.user_name || 'Unknown user'}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>{row.user_email}</div>
                      <div style={{ display: 'flex', gap: 16 }}>
                        <div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: '#f8fafc' }}>{Number(row.total_enrichments).toLocaleString()}</div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>enrichments</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: '#10b981' }}>{Number(row.total_credits_used || 0).toLocaleString()}</div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>credits</div>
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: '#475569', marginTop: 8 }}>
                        Last: {row.last_enrichment_at ? new Date(row.last_enrichment_at).toLocaleDateString() : 'never'}
                      </div>
                    </Card>
                  ))}
                </div>
              )}

              {/* Full log with user filter */}
              <Card>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#f8fafc' }}>
                    All Enrichments
                    {enrichments.pagination?.total > 0 && (
                      <span style={{ marginLeft: 8, fontSize: 12, color: '#64748b', fontWeight: 400 }}>{enrichments.pagination.total.toLocaleString()} total</span>
                    )}
                  </h4>
                  {enrichments.userBreakdown?.length > 1 && (
                    <select
                      onChange={e => { setEnrichmentPage(1); loadEnrichments(1, e.target.value || undefined); }}
                      style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 6, padding: '6px 10px', color: '#f8fafc', fontSize: 12 }}
                    >
                      <option value="">All users</option>
                      {enrichments.userBreakdown.map(row => (
                        <option key={row.userId} value={row.userId}>{row.user_name || row.user_email}</option>
                      ))}
                    </select>
                  )}
                </div>
                {enrichments.enrichments?.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
                    <div style={{ color: '#64748b', fontSize: 14 }}>No enrichments yet</div>
                    <div style={{ color: '#475569', fontSize: 12, marginTop: 4 }}>Records enriched by this org will appear here</div>
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #334155', background: '#0f172a' }}>
                        <th style={{ padding: '10px 20px', textAlign: 'left', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Person Enriched</th>
                        <th style={{ padding: '10px 20px', textAlign: 'left', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>By User</th>
                        <th style={{ padding: '10px 20px', textAlign: 'left', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>LinkedIn</th>
                        <th style={{ padding: '10px 20px', textAlign: 'right', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Credits</th>
                        <th style={{ padding: '10px 20px', textAlign: 'right', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrichments.enrichments.map(e => (
                        <tr key={e.id} style={{ borderBottom: '1px solid #1e293b' }}>
                          <td style={{ padding: '12px 20px' }}>
                            <div style={{ fontWeight: 600, color: '#f8fafc', fontSize: 13 }}>{e.enriched_person || '—'}</div>
                            {e.knine_record_id && <div style={{ fontSize: 11, color: '#475569', fontFamily: 'monospace' }}>{e.knine_record_id}</div>}
                          </td>
                          <td style={{ padding: '12px 20px' }}>
                            <div style={{ fontSize: 13, color: '#f8fafc' }}>{e.user_name || '—'}</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>{e.user_email}</div>
                          </td>
                          <td style={{ padding: '12px 20px' }}>
                            {e.linkedin_url ? (
                              <a href={e.linkedin_url} target="_blank" rel="noreferrer" style={{ color: '#0a66c2', fontSize: 12 }}>View Profile</a>
                            ) : <span style={{ color: '#334155', fontSize: 12 }}>—</span>}
                          </td>
                          <td style={{ padding: '12px 20px', textAlign: 'right', color: '#10b981', fontWeight: 600, fontSize: 13 }}>{e.credits_charged ?? 1}</td>
                          <td style={{ padding: '12px 20px', textAlign: 'right', color: '#64748b', fontSize: 12 }}>{new Date(e.created_at).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {enrichments.pagination?.totalPages > 1 && (
                  <div style={{ padding: '12px 20px', borderTop: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#64748b', fontSize: 13 }}>Page {enrichmentPage} of {enrichments.pagination.totalPages}</span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button variant="secondary" size="sm" icon={ChevronLeft} onClick={() => { setEnrichmentPage(p => p - 1); loadEnrichments(enrichmentPage - 1); }} disabled={enrichmentPage <= 1} />
                      <Button variant="secondary" size="sm" icon={ChevronRight} onClick={() => { setEnrichmentPage(p => p + 1); loadEnrichments(enrichmentPage + 1); }} disabled={enrichmentPage >= enrichments.pagination.totalPages} />
                    </div>
                  </div>
                )}
              </Card>
            </>
          ) : null}
        </div>
      )}

      {activeTab === 'loginActivity' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Login Activity</h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Login history and security overview for this organization (last 30 days)</p>
            </div>
            <Button variant="secondary" icon={RefreshCw} onClick={loadLoginActivity} disabled={loginActivityLoading}>Refresh</Button>
          </div>

          {loginActivityLoading && !loginActivity ? <LoadingSpinner text="Loading login activity..." /> : loginActivity ? (
            <>
              {/* Stats strip */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14, marginBottom: 20 }}>
                {[
                  { label: 'Total Logins', value: loginActivity.stats?.total_logins || 0, color: '#f8fafc' },
                  { label: 'Successful', value: loginActivity.stats?.successful_logins || 0, color: '#10b981' },
                  { label: 'Failed', value: loginActivity.stats?.failed_logins || 0, color: '#ef4444' },
                  { label: 'Unique Users', value: loginActivity.stats?.unique_users || 0, color: '#93c5fd' },
                  { label: 'Unique IPs', value: loginActivity.stats?.unique_ips || 0, color: '#a78bfa' },
                ].map(s => (
                  <Card key={s.label} style={{ padding: '14px 16px', textAlign: 'center' }}>
                    <div style={{ fontSize: 26, fontWeight: 800, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{s.label}</div>
                  </Card>
                ))}
              </div>

              {/* Per-user summary */}
              {loginActivity.userSummary?.length > 0 && (
                <Card style={{ marginBottom: 20 }}>
                  <div style={{ padding: '14px 20px', borderBottom: '1px solid #334155' }}>
                    <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#f8fafc' }}>Per-User Summary</h4>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #334155' }}>
                        <th style={{ padding: '10px 20px', textAlign: 'left', fontSize: 12, color: '#64748b', fontWeight: 600 }}>User</th>
                        <th style={{ padding: '10px 20px', textAlign: 'right', fontSize: 12, color: '#64748b', fontWeight: 600 }}>Total</th>
                        <th style={{ padding: '10px 20px', textAlign: 'right', fontSize: 12, color: '#10b981', fontWeight: 600 }}>OK</th>
                        <th style={{ padding: '10px 20px', textAlign: 'right', fontSize: 12, color: '#ef4444', fontWeight: 600 }}>Failed</th>
                        <th style={{ padding: '10px 20px', textAlign: 'right', fontSize: 12, color: '#64748b', fontWeight: 600 }}>Last Login</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loginActivity.userSummary.map(u => (
                        <tr key={u.user_id} style={{ borderBottom: '1px solid #1e293b' }}>
                          <td style={{ padding: '10px 20px' }}>
                            <div style={{ fontWeight: 600, color: '#f8fafc', fontSize: 13 }}>{u.name || '—'}</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>{u.email}</div>
                          </td>
                          <td style={{ padding: '10px 20px', textAlign: 'right', color: '#f8fafc' }}>{u.total_logins}</td>
                          <td style={{ padding: '10px 20px', textAlign: 'right', color: '#10b981' }}>{u.successful_logins}</td>
                          <td style={{ padding: '10px 20px', textAlign: 'right', color: u.failed_logins > 0 ? '#ef4444' : '#64748b' }}>{u.failed_logins}</td>
                          <td style={{ padding: '10px 20px', textAlign: 'right', color: '#64748b', fontSize: 12 }}>
                            {u.last_successful_login ? new Date(u.last_successful_login).toLocaleString() : 'Never'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              )}

              {/* Full login log */}
              <Card>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid #334155' }}>
                  <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#f8fafc' }}>
                    Recent Login Events
                    <span style={{ marginLeft: 8, fontSize: 12, color: '#64748b', fontWeight: 400 }}>
                      {loginActivity.pagination?.total?.toLocaleString()} total
                    </span>
                  </h4>
                </div>
                {!loginActivity.logins?.length ? (
                  <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No login history yet</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #334155' }}>
                        <th style={{ padding: '10px 20px', textAlign: 'left', fontSize: 12, color: '#64748b', fontWeight: 600 }}>User</th>
                        <th style={{ padding: '10px 20px', textAlign: 'left', fontSize: 12, color: '#64748b', fontWeight: 600 }}>Result</th>
                        <th style={{ padding: '10px 20px', textAlign: 'left', fontSize: 12, color: '#64748b', fontWeight: 600 }}>IP Address</th>
                        <th style={{ padding: '10px 20px', textAlign: 'left', fontSize: 12, color: '#64748b', fontWeight: 600 }}>Failure Reason</th>
                        <th style={{ padding: '10px 20px', textAlign: 'right', fontSize: 12, color: '#64748b', fontWeight: 600 }}>Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loginActivity.logins.map((l, i) => (
                        <tr key={l.id || i} style={{ borderBottom: '1px solid #1e293b' }}>
                          <td style={{ padding: '10px 20px' }}>
                            <div style={{ fontSize: 13, color: '#f8fafc' }}>{l.user_name || '—'}</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>{l.user_email}</div>
                          </td>
                          <td style={{ padding: '10px 20px' }}>
                            <Badge variant={l.success ? 'success' : 'danger'}>{l.success ? 'Success' : 'Failed'}</Badge>
                          </td>
                          <td style={{ padding: '10px 20px', color: '#94a3b8', fontSize: 12, fontFamily: 'monospace' }}>{l.ip_address || '—'}</td>
                          <td style={{ padding: '10px 20px', color: '#fbbf24', fontSize: 12 }}>{l.failure_reason || '—'}</td>
                          <td style={{ padding: '10px 20px', textAlign: 'right', color: '#64748b', fontSize: 12 }}>{new Date(l.createdAt).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
            </>
          ) : null}
        </div>
      )}

      {activeTab === 'platforms' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Platform Access</h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Control which kNine platforms this organization can access</p>
            </div>
            <Button variant="secondary" icon={RefreshCw} onClick={loadPlatforms} disabled={platformsLoading}>Refresh</Button>
          </div>

          {platformsLoading && !platforms ? <LoadingSpinner text="Loading platforms..." /> : platforms?.error ? (
            <Card style={{ padding: 24 }}><p style={{ color: '#ef4444' }}>Error: {platforms.error}</p></Card>
          ) : platforms ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {[
                { key: 'roxi',         label: 'Roxi',         description: 'AI-powered search & sourcing', always: true },
                { key: 'cooper',       label: 'Cooper',       description: 'AI-powered company & lead intelligence' },
                { key: 'the_yard',     label: 'The Yard',     description: 'Interactive visualization dashboard' },
                { key: 'tracker',      label: 'Tracker',      description: 'Contact job-change monitoring (add-on)' },
                { key: 'fee_recovery', label: 'Fee Recovery', description: 'Placement fee identification (add-on)' },
              ].map(p => {
                const controls = platforms.platformControls || platforms.platform_controls || {};
                const ctrl = controls[p.key] || {};
                const isEnabled = ctrl.enabled !== false;
                return (
                  <Card key={p.key} style={{ padding: 20, opacity: p.always ? 0.85 : 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: 700, color: '#f8fafc', fontSize: 15, marginBottom: 4 }}>{p.label}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{p.description}</div>
                        {!isEnabled && ctrl.disabled_at && (
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                            Disabled {new Date(ctrl.disabled_at).toLocaleDateString()}
                            {ctrl.reason ? ` — ${ctrl.reason}` : ''}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => !p.always && togglePlatform(p.key, !isEnabled)}
                        disabled={p.always}
                        title={p.always ? 'Roxi is always enabled' : undefined}
                        style={{
                          padding: '6px 14px',
                          borderRadius: 8,
                          border: 'none',
                          cursor: p.always ? 'not-allowed' : 'pointer',
                          fontWeight: 600,
                          fontSize: 12,
                          background: isEnabled ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.12)',
                          color: isEnabled ? '#10b981' : '#ef4444',
                          opacity: p.always ? 0.6 : 1,
                        }}
                      >
                        {isEnabled ? 'Enabled' : 'Disabled'}
                      </button>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : null}
        </div>
      )}

      {activeTab === 'modules' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Module Management</h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Enable or disable product modules for this organization</p>
            </div>
            <Button variant="secondary" icon={RefreshCw} onClick={loadModules} disabled={modulesLoading}>Refresh</Button>
          </div>

          {modulesLoading && !modules ? <LoadingSpinner text="Loading modules..." /> : modules?.error ? (
            <Card style={{ padding: 24 }}><p style={{ color: '#ef4444' }}>Error: {modules.error}</p></Card>
          ) : modules ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
                {(modules.modules || []).map(mod => (
                  <Card key={mod.key} style={{ padding: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <div>
                        <div style={{ fontWeight: 700, color: '#f8fafc', fontSize: 14 }}>{mod.name}</div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{mod.description}</div>
                      </div>
                      <Badge variant={mod.isEnabled ? 'success' : 'default'}>{mod.isEnabled ? 'On' : 'Off'}</Badge>
                    </div>
                    <div style={{ fontSize: 11, color: '#475569', marginBottom: 12 }}>
                      Source: <span style={{ color: '#94a3b8' }}>{mod.source}</span>
                      {mod.isAddon && <span style={{ marginLeft: 8, color: '#f59e0b' }}>Add-on</span>}
                    </div>
                    <button
                      onClick={() => toggleModule(mod.key, !mod.isEnabled)}
                      style={{
                        width: '100%',
                        padding: '7px 0',
                        borderRadius: 8,
                        border: '1px solid',
                        borderColor: mod.isEnabled ? '#334155' : '#0a66c2',
                        background: mod.isEnabled ? 'transparent' : 'rgba(10,102,194,0.12)',
                        color: mod.isEnabled ? '#94a3b8' : '#60a5fa',
                        fontWeight: 600,
                        fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      {mod.isEnabled ? 'Disable' : 'Enable'}
                    </button>
                  </Card>
                ))}
              </div>
            </>
          ) : null}
        </div>
      )}

      {activeTab === 'features' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Feature Management</h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Override plan features for this organization</p>
            </div>
            <Button variant="secondary" icon={RefreshCw} onClick={loadFeatures}>Refresh</Button>
          </div>
          {featureError && <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', marginBottom: 16 }}><div style={{ width: 24, height: 24, borderRadius: 6, background: 'linear-gradient(135deg, #ef4444, #1565C0)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>K9</div><span style={{ fontSize: 13, color: '#fca5a5' }}>{featureError}</span></div>}
          {features ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
              {features.categories?.map(category => (
                <Card key={category} style={{ padding: 20 }}>
                  <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>{category}</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {features.features?.filter(f => f.category === category).map(feature => (
                      <div key={feature.feature_key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 12, background: '#0f172a', borderRadius: 8 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 500, color: '#f8fafc', marginBottom: 2 }}>{feature.display_name}</div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>
                            {feature.source === 'override' ? (
                              <span style={{ color: '#f59e0b' }}>Override by {feature.override?.setBy}</span>
                            ) : feature.source === 'addon' ? (
                              <span style={{ color: '#10b981' }}>Add-on</span>
                            ) : (
                              <span>Via {plan} plan</span>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Badge variant={feature.isEnabled ? 'success' : 'danger'}>{feature.isEnabled ? 'On' : 'Off'}</Badge>
                          <button
                            onClick={async () => {
                              try {
                                setFeatureError(null);
                                await adminApiCall(`/admin/customers/${customerId}/features`, {
                                  method: 'POST',
                                  body: JSON.stringify({ featureKey: feature.feature_key, enabled: !feature.isEnabled, reason: 'Admin override' })
                                });
                                loadFeatures();
                              } catch (err) { setFeatureError(err.message); }
                            }}
                            style={{ padding: '4px 8px', background: '#334155', border: 'none', borderRadius: 4, color: '#f8fafc', cursor: 'pointer', fontSize: 11 }}
                          >
                            Toggle
                          </button>
                          {feature.override && (
                            <button
                              onClick={async () => {
                                try {
                                  setFeatureError(null);
                                  await adminApiCall(`/admin/customers/${customerId}/features/${feature.feature_key}`, { method: 'DELETE' });
                                  loadFeatures();
                                } catch (err) { setFeatureError(err.message); }
                              }}
                              style={{ padding: '4px 8px', background: '#7f1d1d', border: 'none', borderRadius: 4, color: '#fca5a5', cursor: 'pointer', fontSize: 11 }}
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <LoadingSpinner text="Loading features..." />
          )}
        </div>
      )}

      {/* ================================================================
          SEARCH CONFIG TAB
          ================================================================ */}
      {activeTab === 'searchConfig' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Search Configuration</h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
                Configure Roxi and Cooper result limits per plan tier. Trial defaults: Roxi 10 / Cooper 5. Paid defaults: Roxi 50 / Cooper 10.
              </p>
            </div>
            <Button variant="secondary" icon={RefreshCw} onClick={loadSearchConfig} disabled={searchConfigLoading}>Refresh</Button>
          </div>

          {searchConfigError && (
            <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', marginBottom: 16, color: '#fca5a5', fontSize: 13 }}>
              {searchConfigError}
            </div>
          )}

          {searchConfigLoading ? <LoadingSpinner text="Loading search config..." /> : searchConfig ? (
            <div style={{ display: 'grid', gap: 20 }}>
              {/* Status banner */}
              <div style={{ padding: '10px 16px', borderRadius: 10, background: searchConfig.isTrial ? 'rgba(245,158,11,0.1)' : 'rgba(16,185,129,0.1)', border: `1px solid ${searchConfig.isTrial ? 'rgba(245,158,11,0.3)' : 'rgba(16,185,129,0.3)'}`, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 13, color: searchConfig.isTrial ? '#fbbf24' : '#34d399', fontWeight: 600 }}>
                  {searchConfig.isTrial ? '🔶 Trial Account' : '✅ Paid Account'} — {searchConfig.subscriptionTier}
                </span>
              </div>

              {/* Roxi People Search limits */}
              <SearchLimitCard
                title="Roxi — People Search"
                description="Controls how many people search results are returned per search. Trial: 10, Paid: 50 by default."
                isTrial={searchConfig.isTrial}
                config={searchConfig.knine}
                onSave={async (vals) => {
                  await saveSearchConfig({
                    knineOverrideLimit: vals.overrideLimit,
                    knineTrialLimit: vals.trialLimit,
                    kninePaidLimit: vals.paidLimit,
                  });
                }}
                saving={searchConfigSaving}
              />

              {/* Cooper Company Search limits */}
              <SearchLimitCard
                title="Cooper Company Search"
                description="Controls how many company results are returned per BD search. Trial: 5, Paid: 10 by default."
                isTrial={searchConfig.isTrial}
                config={searchConfig.cooper}
                onSave={async (vals) => {
                  await saveSearchConfig({
                    cooperOverrideLimit: vals.overrideLimit,
                    cooperTrialLimit: vals.trialLimit,
                    cooperPaidLimit: vals.paidLimit,
                  });
                }}
                saving={searchConfigSaving}
              />

              {/* Per-user overrides */}
              <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div>
                    <h4 style={{ margin: 0, color: '#f8fafc', fontWeight: 600 }}>Per-User Overrides</h4>
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748b' }}>Override limits for individual users. Leave blank to use org defaults.</p>
                  </div>
                  <UserSearchOverrideForm
                    users={data.users || []}
                    customerId={customerId}
                    onSaved={loadSearchConfig}
                  />
                </div>
                {(searchConfig.userOverrides || []).length === 0 ? (
                  <p style={{ color: '#475569', fontSize: 13, margin: 0 }}>No per-user overrides set.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {searchConfig.userOverrides.map(uo => (
                      <div key={uo.userId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#0f172a', borderRadius: 8 }}>
                        <div>
                          <span style={{ fontWeight: 500, color: '#f8fafc' }}>{uo.user_name || uo.userId}</span>
                          <span style={{ color: '#64748b', fontSize: 12, marginLeft: 8 }}>{uo.user_email}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          {uo.knine_result_limit != null && <Badge variant="info">Roxi: {uo.knine_result_limit}</Badge>}
                          {uo.cooper_result_limit != null && <Badge variant="info">Cooper: {uo.cooper_result_limit}</Badge>}
                          {uo.override_reason && <span style={{ fontSize: 11, color: '#94a3b8' }}>{uo.override_reason}</span>}
                          <button
                            onClick={() => removeUserSearchOverride(uo.userId)}
                            style={{ padding: '3px 8px', background: '#7f1d1d', border: 'none', borderRadius: 4, color: '#fca5a5', cursor: 'pointer', fontSize: 11 }}
                          >Remove</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          ) : null}
        </div>
      )}

      {/* ================================================================
          HUBSPOT DEALS / CONTRACT TAB
          ================================================================ */}
      {activeTab === 'hubspot' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>HubSpot Deals & Contracts</h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Live view of HubSpot deals and agreements associated with this customer.</p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {hubspotData?.cachedAt && (
                <span style={{ fontSize: 12, color: '#64748b', alignSelf: 'center' }}>
                  {hubspotData.fromCache ? 'Cached' : 'Live'} · {new Date(hubspotData.cachedAt).toLocaleTimeString()}
                </span>
              )}
              <Button variant="secondary" icon={RefreshCw} onClick={() => loadHubspot(true)} disabled={hubspotLoading}>Refresh</Button>
              {hubspotData?.companyId && (
                <Button variant="secondary" icon={ExternalLink} onClick={() => window.open(`https://app.hubspot.com/contacts/companies/${hubspotData.companyId}`, '_blank')}>
                  Open in HubSpot
                </Button>
              )}
            </div>
          </div>

          {/* HubSpot Company ID — always editable so admins can link or correct it at any time */}
          <HubSpotIdEditor customerId={customerId} currentId={hubspotData?.companyId || organization?.hubspot_company_id} onSaved={() => loadHubspot(true)} />

          {hubspotLoading ? <LoadingSpinner text="Fetching HubSpot data..." /> : hubspotData ? (
            <div>
              {hubspotData.error && (
                <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', marginBottom: 16, color: '#fca5a5', fontSize: 13 }}>
                  {hubspotData.error}
                </div>
              )}
              {!hubspotData.companyId && !hubspotData.error && (
                <LinkHubSpotPanel customerId={customerId} onLinked={() => loadHubspot(true)} />
              )}
              {(hubspotData.deals || []).length === 0 && hubspotData.companyId && (
                <div style={{ textAlign: 'center', padding: 60, color: '#475569' }}>
                  <FileText size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
                  <p>No deals found in HubSpot for this company.</p>
                </div>
              )}
              <div style={{ display: 'grid', gap: 16 }}>
                {(hubspotData.deals || []).map(deal => (
                  <Card key={deal.id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                          <h4 style={{ margin: 0, color: '#f8fafc', fontWeight: 600 }}>{deal.name}</h4>
                          <Badge variant={
                            deal.stage?.includes('closed_won') ? 'success' :
                            deal.stage?.includes('closed_lost') ? 'danger' : 'info'
                          }>{deal.stage || 'Unknown Stage'}</Badge>
                          {deal.contractType && <Badge variant="warning">{deal.contractType}</Badge>}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                          {deal.amount != null && (
                            <div>
                              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>Deal Value</div>
                              <div style={{ color: '#10b981', fontWeight: 600 }}>${deal.amount.toLocaleString()}</div>
                            </div>
                          )}
                          {deal.closeDate && (
                            <div>
                              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>Close Date</div>
                              <div style={{ color: '#f8fafc', fontSize: 13 }}>{new Date(deal.closeDate).toLocaleDateString()}</div>
                            </div>
                          )}
                          {deal.probability != null && (
                            <div>
                              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>Probability</div>
                              <div style={{ color: '#f8fafc', fontSize: 13 }}>{Math.round(deal.probability * 100)}%</div>
                            </div>
                          )}
                          {deal.createDate && (
                            <div>
                              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>Created</div>
                              <div style={{ color: '#f8fafc', fontSize: 13 }}>{new Date(deal.createDate).toLocaleDateString()}</div>
                            </div>
                          )}
                          {deal.pipeline && (
                            <div>
                              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>Pipeline</div>
                              <div style={{ color: '#f8fafc', fontSize: 13 }}>{deal.pipeline}</div>
                            </div>
                          )}
                        </div>
                        {deal.description && (
                          <p style={{ margin: '10px 0 0', fontSize: 13, color: '#94a3b8' }}>{deal.description}</p>
                        )}
                      </div>
                      <a
                        href={deal.hubspotUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{ marginLeft: 16, padding: '6px 12px', background: '#1e3a5f', border: '1px solid #334155', borderRadius: 6, color: '#60a5fa', fontSize: 12, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}
                      >
                        <ExternalLink size={12} /> Open Deal
                      </a>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}

      {activeTab === 'credits' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Credit Management</h3>
            <Button variant="primary" icon={Plus} onClick={() => setCreditModal(true)}>Add Credits</Button>
          </div>

          {/* Credit Summary Cards */}
          <div className="kubi-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
            <Card style={{ padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 800, color: '#10b981' }}>{credits.total?.toLocaleString() || 0}</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>Total Available</div>
            </Card>
            <Card style={{ padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 800, color: '#0a66c2' }}>{credits.subscription?.toLocaleString() || 0}</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>Subscription Credits</div>
            </Card>
            <Card style={{ padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 800, color: '#f59e0b' }}>{credits.purchased?.toLocaleString() || 0}</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>Purchased Credits</div>
            </Card>
            <Card style={{ padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 800, color: '#8b5cf6' }}>{credits.annualAllowance?.toLocaleString() || 0}</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>Annual Allowance</div>
            </Card>
          </div>

          {/* Per-User Usage */}
          {creditUsage && (
            <Card style={{ padding: 24, marginBottom: 24 }}>
              <h4 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#f8fafc' }}>Usage by User (This Period)</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #334155' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>User</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Actions</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Credits Used</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Limit</th>
                  </tr>
                </thead>
                <tbody>
                  {creditUsage.userUsage?.map(user => (
                    <tr key={user.id} style={{ borderBottom: '1px solid #334155' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 500, color: '#f8fafc' }}>{user.name || user.email}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{user.email}</div>
                      </td>
                      <td style={{ padding: '12px 16px', color: '#f8fafc' }}>{user.total_actions || 0}</td>
                      <td style={{ padding: '12px 16px', color: '#f59e0b', fontWeight: 600 }}>{user.credits_used || 0}</td>
                      <td style={{ padding: '12px 16px', color: '#64748b' }}>
                        {user.creditAllocationType === 'fixed' ? `${user.creditAllocationValue}` : user.creditAllocationType === 'percentage' ? `${user.creditAllocationValue}%` : 'None'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {/* Usage by Action Type */}
          {creditUsage?.byActionType && creditUsage.byActionType.length > 0 && (
            <Card style={{ padding: 24, marginBottom: 24 }}>
              <h4 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#f8fafc' }}>Usage by Action Type</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                {creditUsage.byActionType.map(action => (
                  <div key={action.actionType} style={{ padding: 16, background: '#0f172a', borderRadius: 10 }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#f8fafc' }}>{action.credits_used}</div>
                    <div style={{ fontSize: 13, color: '#64748b', textTransform: 'capitalize' }}>{action.actionType?.replace(/_/g, ' ')}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{action.count} actions</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Credit Transactions */}
          <Card style={{ padding: 24 }}>
            <h4 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#f8fafc' }}>Transaction History</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #334155' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Date</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Type</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Amount</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Balance After</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {creditTransactions.map(tx => (
                  <tr key={tx.id} style={{ borderBottom: '1px solid #334155' }}>
                    <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 13 }}>{new Date(tx.createdAt).toLocaleString()}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <Badge variant={tx.transaction_type?.includes('grant') || tx.transaction_type?.includes('purchase') ? 'success' : tx.transaction_type?.includes('usage') ? 'info' : 'warning'}>
                        {tx.transaction_type}
                      </Badge>
                    </td>
                    <td style={{ padding: '12px 16px', color: tx.amount > 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                      {tx.amount > 0 ? '+' : ''}{tx.amount}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#f8fafc' }}>{tx.balance_after}</td>
                    <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 13 }}>{tx.reason || '-'}</td>
                  </tr>
                ))}
                {creditTransactions.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No transactions yet</td>
                  </tr>
                )}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {activeTab === 'searches' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Team Search History</h3>
            <Button variant="secondary" icon={RefreshCw} onClick={loadSearches}>Refresh</Button>
          </div>

          {searches ? (
            <>
              {/* Search Stats */}
              {searches.stats && searches.stats.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
                  {searches.stats.map(stat => (
                    <Card key={stat.search_type} style={{ padding: 20 }}>
                      <div style={{ fontSize: 28, fontWeight: 800, color: '#f8fafc' }}>{stat.count}</div>
                      <div style={{ fontSize: 13, color: '#64748b', textTransform: 'capitalize' }}>{stat.search_type?.replace(/_/g, ' ')}</div>
                      <div style={{ fontSize: 12, color: '#f59e0b' }}>{stat.credits_used} credits used</div>
                    </Card>
                  ))}
                </div>
              )}

              {/* Search History Table */}
              <Card>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #334155' }}>
                      <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Time</th>
                      <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>User</th>
                      <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Type</th>
                      <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Query</th>
                      <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Results</th>
                      <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Credits</th>
                      <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searches.searches?.map(search => {
                      const isExpanded = expandedSearch === search.id;
                      const searchType = search.searchType || search.searchSource || search.search_type || search.actionType || 'search';
                      const query = search.searchQuery || search.query_text || search.search_query || search.query || '-';
                      const resultCount = search.resultsCount ?? search.results_count ?? 0;
                      const creditsUsed = search.credits_used ?? 1;
                      const parsedFilters = search.parsedFilters ? (typeof search.parsedFilters === 'string' ? JSON.parse(search.parsedFilters) : search.parsedFilters) : null;
                      const searchResults = search.searchResults ? (typeof search.searchResults === 'string' ? JSON.parse(search.searchResults) : search.searchResults) : null;
                      const aiInterp = search.aiInterpretation;
                      return (
                        <React.Fragment key={search.id}>
                          <tr
                            style={{ borderBottom: isExpanded ? 'none' : '1px solid #334155', cursor: 'pointer', background: isExpanded ? 'rgba(10,102,194,0.06)' : 'transparent' }}
                            onClick={() => setExpandedSearch(isExpanded ? null : search.id)}
                          >
                            <td style={{ padding: '14px 20px', color: '#64748b', fontSize: 13 }}>{new Date(search.createdAt).toLocaleString()}</td>
                            <td style={{ padding: '14px 20px' }}>
                              <div style={{ fontWeight: 500, color: '#f8fafc', fontSize: 13 }}>{search.user_name || 'Unknown'}</div>
                              <div style={{ fontSize: 12, color: '#64748b' }}>{search.user_email}</div>
                            </td>
                            <td style={{ padding: '14px 20px' }}>
                              <Badge variant="info">{searchType.replace(/_/g, ' ')}</Badge>
                            </td>
                            <td style={{ padding: '14px 20px', color: '#f8fafc', fontSize: 13, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {query}
                            </td>
                            <td style={{ padding: '14px 20px', color: '#f8fafc', fontWeight: 600 }}>{resultCount}</td>
                            <td style={{ padding: '14px 20px', color: '#f59e0b', fontWeight: 600 }}>{creditsUsed}</td>
                            <td style={{ padding: '14px 20px' }}>
                              <span style={{ color: '#0a66c2', fontSize: 12 }}>{isExpanded ? '▲ Hide' : '▼ View'}</span>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr style={{ borderBottom: '1px solid #334155' }}>
                              <td colSpan={7} style={{ padding: '0 20px 20px' }}>
                                <div style={{ background: '#0f172a', borderRadius: 10, padding: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                                  {/* Parsed Filters */}
                                  {parsedFilters && (
                                    <div>
                                      <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Search Filters</div>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        {parsedFilters.job_title && <div style={{ fontSize: 13 }}><span style={{ color: '#64748b' }}>Title: </span><span style={{ color: '#f8fafc' }}>{parsedFilters.job_title}</span></div>}
                                        {parsedFilters.locations?.length > 0 && <div style={{ fontSize: 13 }}><span style={{ color: '#64748b' }}>Location: </span><span style={{ color: '#f8fafc' }}>{parsedFilters.locations.map(l => [l.locality, l.region, l.state].filter(Boolean).join(', ')).join(' | ')}</span></div>}
                                        {parsedFilters.skills?.length > 0 && <div style={{ fontSize: 13 }}><span style={{ color: '#64748b' }}>Skills: </span><span style={{ color: '#f8fafc' }}>{parsedFilters.skills.slice(0, 5).join(', ')}</span></div>}
                                        {parsedFilters.industries?.length > 0 && <div style={{ fontSize: 13 }}><span style={{ color: '#64748b' }}>Industry: </span><span style={{ color: '#f8fafc' }}>{parsedFilters.industries.join(', ')}</span></div>}
                                        {(parsedFilters.experience?.min_years || parsedFilters.experience?.max_years) && <div style={{ fontSize: 13 }}><span style={{ color: '#64748b' }}>Experience: </span><span style={{ color: '#f8fafc' }}>{parsedFilters.experience.min_years || 0}–{parsedFilters.experience.max_years || '∞'} yrs</span></div>}
                                        {parsedFilters.seniority_levels?.length > 0 && <div style={{ fontSize: 13 }}><span style={{ color: '#64748b' }}>Seniority: </span><span style={{ color: '#f8fafc' }}>{parsedFilters.seniority_levels.join(', ')}</span></div>}
                                        {parsedFilters.titles?.length > 0 && <div style={{ fontSize: 13 }}><span style={{ color: '#64748b' }}>Titles: </span><span style={{ color: '#f8fafc' }}>{parsedFilters.titles.slice(0, 5).join(', ')}</span></div>}
                                        {aiInterp && <div style={{ fontSize: 13, marginTop: 4 }}><span style={{ color: '#64748b' }}>AI: </span><span style={{ color: '#94a3b8', fontStyle: 'italic' }}>{aiInterp}</span></div>}
                                      </div>
                                    </div>
                                  )}
                                  {/* Top Results */}
                                  <div>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
                                      Top Results ({resultCount} total{search.atsCount != null ? `, ${search.atsCount} ATS` : ''}{search.knineCount != null ? `, ${search.knineCount} Roxi` : ''})
                                    </div>
                                    {searchResults && Array.isArray(searchResults) && searchResults.length > 0 ? (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {searchResults.slice(0, 5).map((r, ri) => (
                                          <div key={ri} style={{ padding: '8px 12px', background: '#1e293b', borderRadius: 8, fontSize: 13 }}>
                                            <div style={{ fontWeight: 600, color: '#f8fafc' }}>{r.name || r.fullName || 'Unknown'}</div>
                                            <div style={{ color: '#64748b' }}>{[r.title || r.job_title, r.company || r.job_company_name].filter(Boolean).join(' @ ')}</div>
                                            <div style={{ color: '#64748b', fontSize: 12 }}>{r.location || [r.city, r.state].filter(Boolean).join(', ')}</div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div style={{ color: '#64748b', fontSize: 13 }}>No result details stored</div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {(!searches.searches || searches.searches.length === 0) && (
                      <tr>
                        <td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No searches recorded yet</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Card>
            </>
          ) : (
            <LoadingSpinner text="Loading search history..." />
          )}
        </div>
      )}

      {activeTab === 'activity' && (
        <Card style={{ padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Activity Log</h3>
            <Button variant="secondary" icon={RefreshCw} onClick={loadActivity}>Refresh</Button>
          </div>
          {activityLoading ? (
            <LoadingSpinner text="Loading activity..." />
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #334155' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Time</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>User</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Type</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Action</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Detail</th>
                </tr>
              </thead>
              <tbody>
                {(activity?.activities || []).map((item, i) => (
                  <tr key={item.id || i} style={{ borderBottom: '1px solid #334155' }}>
                    <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 13 }}>
                      {new Date(item.createdAt).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontWeight: 500, color: '#f8fafc', fontSize: 13 }}>{item.user_name || 'Unknown'}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>{item.user_email}</div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <Badge variant={
                        item.activity_type === 'search' ? 'info' :
                        item.activity_type === 'credit_usage' ? 'warning' : 'default'
                      }>{item.activity_type?.replace(/_/g, ' ')}</Badge>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#f8fafc', fontSize: 13 }}>
                      {item.action?.replace(/_/g, ' ')}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 13 }}>
                      {item.activity_type === 'credit_usage' ? `${item.value} credit(s)` :
                       item.activity_type === 'search' ? `${item.value || 0} results` :
                       item.activity_type === 'login' ? item.value : ''}
                    </td>
                  </tr>
                ))}
                {(!activity || activity.activities?.length === 0) && (
                  <tr>
                    <td colSpan={5} style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No activity recorded yet</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {activeTab === 'notes' && (
        <Card style={{ padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Internal Notes</h3>
            <Button variant="secondary" icon={Plus} onClick={() => setNoteModal(true)}>Add Note</Button>
          </div>
          {notes.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {notes.map(note => (
                <div key={note.id} style={{ padding: 16, background: '#0f172a', borderRadius: 10, border: note.is_pinned ? '1px solid #f59e0b' : 'none' }}>
                  {note.is_pinned && <Badge variant="warning" style={{ marginBottom: 8 }}>Pinned</Badge>}
                  <div style={{ color: '#f8fafc', marginBottom: 12, whiteSpace: 'pre-wrap' }}>{note.note}</div>
                  <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#64748b' }}>
                    <span>{note.admin_name}</span>
                    <span>•</span>
                    <span>{new Date(note.createdAt).toLocaleString()}</span>
                    <span>•</span>
                    <Badge variant="default" style={{ fontSize: 11 }}>{note.note_type}</Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: '#64748b', textAlign: 'center', padding: 40 }}>No notes yet. Add one to keep track of important customer information.</div>
          )}
        </Card>
      )}

      {/* Credit Modal */}
      {creditModal && (
        <CreditModal customerId={customerId} onClose={() => setCreditModal(false)} onSuccess={() => { loadCustomer(); loadCreditUsage(); }} />
      )}

      {/* Note Modal */}
      {noteModal && (
        <NoteModal customerId={customerId} onClose={() => setNoteModal(false)} onSuccess={loadCustomer} />
      )}

      {/* Impersonate Modal */}
      {impersonateModal && (
        <ImpersonateModal users={users} defaultUserId={impersonateDefaultUser} onClose={() => { setImpersonateModal(false); setImpersonateDefaultUser(''); }} />
      )}

      {/* Subscription Modal */}
      {subscriptionModal && (
        <SubscriptionModal customerId={customerId} currentPlan={plan} onClose={() => setSubscriptionModal(false)} onSuccess={loadCustomer} />
      )}

      {/* Seat Modal */}
      {seatModal && (
        <SeatModal customerId={customerId} seats={seats} onClose={() => setSeatModal(false)} onSuccess={loadCustomer} />
      )}

      {/* Delete Modal */}
      {deleteModal && (
        <DeleteOrgModal
          org={{ id: customerId, name: organization?.name || customerId }}
          onClose={() => setDeleteModal(false)}
          onDeleted={(msg) => { setDeleteModal(false); onBack(); }}
        />
      )}

      {/* Merge Modal */}
      {mergeModal && (
        <MergeOrgModal
          sourceOrg={{ id: customerId, name: organization?.name || customerId }}
          onClose={() => setMergeModal(false)}
          onMerged={() => { setMergeModal(false); onBack(); }}
        />
      )}
    </div>
  );
};

// =============================================================================
// MODALS
// =============================================================================

const CreditModal = ({ customerId, onClose, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [type, setType] = useState('admin_grant');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      await adminApiCall(`/admin/customers/${customerId}/credits`, {
        method: 'POST',
        body: JSON.stringify({ amount: parseInt(amount), reason, type }),
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <Card style={{ padding: 24, width: 400 }}>
        <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Modify Credits</h3>
        <Input label="Amount" type="number" value={amount} onChange={setAmount} placeholder="Enter amount (positive to add, negative to deduct)" />
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500, color: '#94a3b8' }}>Type</label>
          <select value={type} onChange={e => setType(e.target.value)} style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 14 }}>
            <option value="admin_grant">Admin Grant</option>
            <option value="admin_deduct">Admin Deduct</option>
            <option value="trial_bonus">Trial Bonus</option>
            <option value="refund">Refund</option>
            <option value="compensation">Compensation</option>
          </select>
        </div>
        <Input label="Reason (required)" value={reason} onChange={setReason} placeholder="Enter reason for this credit change" />
        {error && <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', marginTop: 12 }}><div style={{ width: 24, height: 24, borderRadius: 6, background: 'linear-gradient(135deg, #ef4444, #1565C0)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>K9</div><span style={{ fontSize: 13, color: '#fca5a5' }}>{error}</span></div>}
        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <Button variant="secondary" onClick={onClose} style={{ flex: 1 }}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!amount || !reason || loading} style={{ flex: 1 }}>
            {loading ? 'Processing...' : 'Apply'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

const NoteModal = ({ customerId, onClose, onSuccess }) => {
  const [note, setNote] = useState('');
  const [noteType, setNoteType] = useState('general');
  const [isPinned, setIsPinned] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      await adminApiCall(`/admin/customers/${customerId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ note, note_type: noteType, is_pinned: isPinned }),
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <Card style={{ padding: 24, width: 500 }}>
        <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Add Note</h3>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500, color: '#94a3b8' }}>Note Type</label>
          <select value={noteType} onChange={e => setNoteType(e.target.value)} style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 14 }}>
            <option value="general">General</option>
            <option value="support">Support</option>
            <option value="billing">Billing</option>
            <option value="feature_request">Feature Request</option>
            <option value="escalation">Escalation</option>
          </select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500, color: '#94a3b8' }}>Note</label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Enter your note..."
            rows={5}
            style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 14, resize: 'vertical' }}
          />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#f8fafc', cursor: 'pointer' }}>
          <input type="checkbox" checked={isPinned} onChange={e => setIsPinned(e.target.checked)} />
          Pin this note
        </label>
        {error && <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', marginTop: 12 }}><div style={{ width: 24, height: 24, borderRadius: 6, background: 'linear-gradient(135deg, #ef4444, #1565C0)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>K9</div><span style={{ fontSize: 13, color: '#fca5a5' }}>{error}</span></div>}
        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <Button variant="secondary" onClick={onClose} style={{ flex: 1 }}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!note || loading} style={{ flex: 1 }}>
            {loading ? 'Saving...' : 'Save Note'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

const SubscriptionModal = ({ customerId, currentPlan, onClose, onSuccess }) => {
  const [plan, setPlan] = useState(currentPlan);
  const [reason, setReason] = useState('');
  const [applyCredits, setApplyCredits] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const plans = [
    { id: 'trial',        label: 'Trial',                    sub: 'Free limited access' },
    { id: 'starter',      label: 'Starter',                  sub: '$3,000/yr · 2,000 credits' },
    { id: 'professional', label: 'Professional',             sub: '$6,000/yr · 5,000 credits' },
    { id: 'business',     label: 'Business',                 sub: '$10,000/yr · 10,000 credits' },
    { id: 'enterprise',   label: 'Enterprise',               sub: 'Custom pricing · unlimited' },
  ];

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      await adminApiCall(`/admin/customers/${customerId}/subscription`, {
        method: 'POST',
        body: JSON.stringify({ plan, reason, applyCredits }),
      });
      await onSuccess();
      onClose();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <Card style={{ padding: 24, width: 450 }}>
        <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Change Subscription Plan</h3>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500, color: '#94a3b8' }}>Current Plan: <strong style={{ color: '#f8fafc', textTransform: 'capitalize' }}>{currentPlan}</strong></label>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500, color: '#94a3b8' }}>New Plan</label>
          <select value={plan} onChange={e => setPlan(e.target.value)} style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 14 }}>
            {plans.map(p => (
              <option key={p.id} value={p.id}>{p.label} — {p.sub}</option>
            ))}
          </select>
        </div>
        <Input label="Reason (required)" value={reason} onChange={setReason} placeholder="Why is this plan changing?" />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#f8fafc', cursor: 'pointer', marginTop: 12 }}>
          <input type="checkbox" checked={applyCredits} onChange={e => setApplyCredits(e.target.checked)} />
          Apply credit adjustment for plan change
        </label>
        {error && <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', marginTop: 12 }}><div style={{ width: 24, height: 24, borderRadius: 6, background: 'linear-gradient(135deg, #ef4444, #1565C0)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>K9</div><span style={{ fontSize: 13, color: '#fca5a5' }}>{error}</span></div>}
        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <Button variant="secondary" onClick={onClose} style={{ flex: 1 }}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={plan === currentPlan || !reason || loading} style={{ flex: 1 }}>
            {loading ? 'Updating...' : 'Update Plan'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

const SeatModal = ({ customerId, seats, onClose, onSuccess }) => {
  const [action, setAction] = useState('add');
  const [quantity, setQuantity] = useState('1');
  const [reason, setReason] = useState('');
  const [waivePayment, setWaivePayment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      if (action === 'add') {
        await adminApiCall(`/admin/customers/${customerId}/seats`, {
          method: 'POST',
          body: JSON.stringify({ quantity: parseInt(quantity), reason, waivePayment }),
        });
      } else {
        await adminApiCall(`/admin/customers/${customerId}/seats`, {
          method: 'DELETE',
          body: JSON.stringify({ quantity: parseInt(quantity), reason }),
        });
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <Card style={{ padding: 24, width: 450 }}>
        <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Manage Seats</h3>
        <div style={{ padding: 16, background: '#0f172a', borderRadius: 10, marginBottom: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, textAlign: 'center' }}>
            <div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#f8fafc' }}>{seats.baseLimit === -1 ? '∞' : seats.baseLimit}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>Base Limit</div>
            </div>
            <div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#0a66c2' }}>+{seats.additional || 0}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>Additional</div>
            </div>
            <div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#10b981' }}>{seats.effective === -1 ? '∞' : seats.effective}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>Effective</div>
            </div>
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500, color: '#94a3b8' }}>Action</label>
          <select value={action} onChange={e => setAction(e.target.value)} style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 14 }}>
            <option value="add">Add Seats</option>
            <option value="remove">Remove Seats</option>
          </select>
        </div>
        <Input label="Quantity" type="number" value={quantity} onChange={setQuantity} placeholder="Number of seats" />
        <Input label="Reason" value={reason} onChange={setReason} placeholder="Why are seats being changed?" />
        {action === 'add' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#f8fafc', cursor: 'pointer', marginTop: 12 }}>
            <input type="checkbox" checked={waivePayment} onChange={e => setWaivePayment(e.target.checked)} />
            Waive payment (grant free seats)
          </label>
        )}
        {error && <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', marginTop: 12 }}><div style={{ width: 24, height: 24, borderRadius: 6, background: 'linear-gradient(135deg, #ef4444, #1565C0)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>K9</div><span style={{ fontSize: 13, color: '#fca5a5' }}>{error}</span></div>}
        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <Button variant="secondary" onClick={onClose} style={{ flex: 1 }}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!quantity || parseInt(quantity) <= 0 || loading} style={{ flex: 1 }}>
            {loading ? 'Processing...' : action === 'add' ? 'Add Seats' : 'Remove Seats'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

const IMPERSONATE_PAGES = [
  { value: '/settings/team', label: 'Settings → Team (credit allocations, members)' },
  { value: '/settings', label: 'Settings → General' },
  { value: '/settings/usage', label: 'Settings → Usage & Billing' },
  { value: '/settings/integrations', label: 'Settings → Integrations' },
  { value: '/', label: 'Dashboard (home)' },
];

const doImpersonate = async ({ adminApiCallFn, userId, reason, landingPath = '/settings/team' }) => {
  const result = await adminApiCallFn('/admin/impersonate', {
    method: 'POST',
    body: JSON.stringify({ userId, reason }),
  });
  const appUrl = window.location.origin.replace(/\/admin.*$/, '').replace('admin.', 'app.');
  window.open(`${appUrl}${landingPath}?impersonation_token=${result.token}`, '_blank');
  return result;
};

const ImpersonateModal = ({ users, onClose, defaultUserId = '' }) => {
  const [selectedUser, setSelectedUser] = useState(defaultUserId);
  const [reason, setReason] = useState('Admin support session');
  const [landingPath, setLandingPath] = useState('/settings/team');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleImpersonate = async () => {
    setLoading(true);
    setError(null);
    try {
      await doImpersonate({ adminApiCallFn: adminApiCall, userId: selectedUser, reason, landingPath });
      onClose();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <Card style={{ padding: 24, width: 500 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(245, 158, 11, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <AlertTriangle size={22} style={{ color: '#f59e0b' }} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Login as User</h3>
            <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>This action will be logged for security</p>
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500, color: '#94a3b8' }}>Select User</label>
          <select value={selectedUser} onChange={e => setSelectedUser(e.target.value)} style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 14 }}>
            <option value="">Select a user...</option>
            {users.filter(u => u.isActive).map(user => (
              <option key={user.id} value={user.id}>{user.name || user.email} ({user.email})</option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500, color: '#94a3b8' }}>Open To</label>
          <select value={landingPath} onChange={e => setLandingPath(e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 13 }}>
            {IMPERSONATE_PAGES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500, color: '#94a3b8' }}>Reason</label>
          <input
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. Customer requested help with team settings"
            style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 14, boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ padding: 10, borderRadius: 8, background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.2)', marginBottom: 16, fontSize: 12, color: '#f59e0b' }}>
          Opens a new tab where you are fully logged in as this user. All actions are logged.
        </div>
        {error && <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', color: '#fca5a5', fontSize: 13, marginBottom: 12 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 12 }}>
          <Button variant="secondary" onClick={onClose} style={{ flex: 1 }}>Cancel</Button>
          <Button variant="primary" onClick={handleImpersonate} disabled={!selectedUser || !reason.trim() || loading} style={{ flex: 1 }}>
            {loading ? 'Opening...' : 'Open as User'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

// =============================================================================
// AUDIT LOGS PAGE
// =============================================================================

const ACTION_COLORS = {
  login_success: 'success', login_failed: 'danger', login_mfa_failed: 'danger',
  logout: 'default', provision_customer: 'info', delete_org: 'danger',
  restore_org: 'success', org_suspended: 'warning', org_active: 'success',
  change_password: 'warning', '2fa_enabled': 'success', '2fa_disabled': 'warning',
  link_hubspot: 'info', update_customer: 'default', confirm_payment: 'success',
  impersonate_user: 'warning', create_admin: 'info', update_admin: 'default',
};

const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [actionFilter, setActionFilter] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => { loadLogs(); }, [page, actionFilter]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (actionFilter) params.set('action', actionFilter);
      const d = await adminApiCall(`/admin/audit-logs?${params}`);
      setLogs(d.logs || []);
      setPagination(d.pagination);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const filtered = search
    ? logs.filter(l => (l.admin_email || '').toLowerCase().includes(search.toLowerCase()) ||
        l.action?.toLowerCase().includes(search.toLowerCase()) ||
        JSON.stringify(l.details || {}).toLowerCase().includes(search.toLowerCase()))
    : logs;

  const fmtTime = (d) => d ? new Date(d).toLocaleString() : '—';

  const ACTIONS = [
    '', 'login_success', 'login_failed', 'logout', 'provision_customer',
    'delete_org', 'restore_org', 'change_password', '2fa_enabled', '2fa_disabled',
    'link_hubspot', 'update_customer', 'org_suspended', 'org_active',
    'impersonate_user', 'create_admin',
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: '0 0 4px', fontSize: 28, fontWeight: 800, color: '#f8fafc' }}>Audit Logs</h1>
          <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>All admin actions, security events, and system changes</p>
        </div>
        <Button variant="secondary" icon={RefreshCw} onClick={loadLogs} disabled={loading}>Refresh</Button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <select value={actionFilter} onChange={e => { setActionFilter(e.target.value); setPage(1); }}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #334155', background: '#1e293b', color: '#f8fafc', fontSize: 13 }}>
          {ACTIONS.map(a => <option key={a} value={a}>{a || 'All Actions'}</option>)}
        </select>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by admin, action, or details..."
          style={{ flex: 1, minWidth: 220, padding: '8px 12px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 13 }} />
      </div>

      {loading ? <LoadingSpinner text="Loading audit logs..." /> : (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#0f172a' }}>
                {['Time', 'Admin', 'Action', 'Resource', 'IP Address', 'Details'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#64748b' }}>No audit log entries found</td></tr>
              )}
              {filtered.map((log, i) => (
                <tr key={log.id || i} style={{ borderTop: i === 0 ? 'none' : '1px solid #1e293b' }}>
                  <td style={{ padding: '10px 14px', fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>{fmtTime(log.createdAt)}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: '#f8fafc' }}>{log.admin_name || 'System'}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{log.admin_email || ''}</div>
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <Badge variant={ACTION_COLORS[log.action] || 'default'} style={{ fontSize: 11 }}>
                      {log.action?.replace(/_/g, ' ')}
                    </Badge>
                  </td>
                  <td style={{ padding: '10px 14px', fontSize: 12, color: '#94a3b8' }}>
                    {log.resource_type && <div>{log.resource_type}</div>}
                    {log.resource_id && <div style={{ fontFamily: 'monospace', fontSize: 11 }}>{log.resource_id.slice(0, 8)}…</div>}
                  </td>
                  <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b', fontFamily: 'monospace' }}>{log.ip_address || '—'}</td>
                  <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b', maxWidth: 260 }}>
                    {log.details && Object.keys(log.details).length > 0 ? (
                      <details>
                        <summary style={{ cursor: 'pointer', color: '#60a5fa' }}>View details</summary>
                        <pre style={{ margin: '6px 0 0', fontSize: 11, color: '#94a3b8', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      </details>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {pagination && pagination.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <Button variant="secondary" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
          <span style={{ alignSelf: 'center', fontSize: 13, color: '#94a3b8' }}>Page {page} of {pagination.totalPages}</span>
          <Button variant="secondary" onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))} disabled={page === pagination.totalPages}>Next</Button>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// ADMIN USERS PAGE
// =============================================================================

const AdminUsersPage = () => {
  const { admin: currentAdmin } = useAdmin();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('admin');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [toggling, setToggling] = useState(null);

  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 13, boxSizing: 'border-box' };

  useEffect(() => { loadAdmins(); }, []);

  const loadAdmins = async () => {
    setLoading(true);
    try {
      const d = await adminApiCall('/admin/users');
      setAdmins(d.admins || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!newEmail || !newName || !newPassword) { setCreateError('Email, name, and password required'); return; }
    setCreating(true); setCreateError(null);
    try {
      await adminApiCall('/admin/users', { method: 'POST', body: JSON.stringify({ email: newEmail, name: newName, password: newPassword, role: newRole }) });
      setNewEmail(''); setNewName(''); setNewPassword(''); setNewRole('admin');
      setShowCreate(false);
      loadAdmins();
    } catch (err) { setCreateError(err.message); }
    setCreating(false);
  };

  const handleToggle = async (adminUser) => {
    if (adminUser.id === currentAdmin?.id) { alert("You can't deactivate your own account."); return; }
    setToggling(adminUser.id);
    try {
      await adminApiCall(`/admin/users/${adminUser.id}`, { method: 'PUT', body: JSON.stringify({ is_active: !adminUser.is_active }) });
      loadAdmins();
    } catch (err) { alert(err.message); }
    setToggling(null);
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : 'Never';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: '0 0 4px', fontSize: 28, fontWeight: 800, color: '#f8fafc' }}>Admin Users</h1>
          <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>Manage who has access to the kNine admin portal</p>
        </div>
        {currentAdmin?.role === 'super_admin' && (
          <Button variant="primary" icon={Plus} onClick={() => setShowCreate(!showCreate)}>Add Admin</Button>
        )}
      </div>

      {showCreate && (
        <Card style={{ padding: 20, marginBottom: 20 }}>
          <h4 style={{ margin: '0 0 16px', color: '#f8fafc' }}>New Admin Account</h4>
          {createError && <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', color: '#fca5a5', fontSize: 13, marginBottom: 12 }}>{createError}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div><label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: '#94a3b8' }}>Name</label><input value={newName} onChange={e => setNewName(e.target.value)} style={inputStyle} placeholder="Jane Admin" /></div>
            <div><label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: '#94a3b8' }}>Email</label><input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} style={inputStyle} placeholder="jane@knine.ai" /></div>
            <div><label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: '#94a3b8' }}>Temporary Password</label><input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={inputStyle} /></div>
            <div><label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: '#94a3b8' }}>Role</label>
              <select value={newRole} onChange={e => setNewRole(e.target.value)} style={inputStyle}>
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
                <option value="support">Support</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleCreate} disabled={creating}>{creating ? 'Creating...' : 'Create Admin'}</Button>
          </div>
        </Card>
      )}

      {loading ? <LoadingSpinner text="Loading admins..." /> : (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#0f172a' }}>
                {['Admin', 'Role', '2FA', 'Last Login', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {admins.map((a, i) => (
                <tr key={a.id} style={{ borderTop: i === 0 ? 'none' : '1px solid #1e293b' }}>
                  <td style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 34, height: 34, borderRadius: 8, background: a.id === currentAdmin?.id ? 'rgba(10,102,194,0.3)' : '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#f8fafc', fontSize: 14, flexShrink: 0 }}>
                        {a.name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: '#f8fafc', fontSize: 14 }}>{a.name} {a.id === currentAdmin?.id && <span style={{ fontSize: 11, color: '#64748b' }}>(you)</span>}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{a.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    <Badge variant={a.role === 'super_admin' ? 'success' : 'info'} style={{ fontSize: 11 }}>
                      {a.role?.replace('_', ' ')}
                    </Badge>
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    {a.mfa_enabled
                      ? <span style={{ fontSize: 12, color: '#10b981', fontWeight: 600 }}>✓ On</span>
                      : <span style={{ fontSize: 12, color: '#475569' }}>Off</span>}
                  </td>
                  <td style={{ padding: '12px 14px', fontSize: 13, color: '#94a3b8' }}>{fmtDate(a.last_login_at)}</td>
                  <td style={{ padding: '12px 14px' }}>
                    <Badge variant={a.is_active ? 'success' : 'danger'}>{a.is_active ? 'Active' : 'Inactive'}</Badge>
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    {a.id !== currentAdmin?.id && currentAdmin?.role === 'super_admin' && (
                      <button
                        onClick={() => handleToggle(a)}
                        disabled={toggling === a.id}
                        style={{ padding: '5px 12px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          background: a.is_active ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)',
                          color: a.is_active ? '#ef4444' : '#10b981',
                          opacity: toggling === a.id ? 0.5 : 1 }}
                      >
                        {toggling === a.id ? '...' : a.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

// =============================================================================
// DELETE ORG CONFIRMATION MODAL
// =============================================================================

const DeleteOrgModal = ({ org, onClose, onDeleted }) => {
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const inputStyle = { width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 14, boxSizing: 'border-box' };

  const handleDelete = async () => {
    if (confirmText !== 'delete') { setError('Type "delete" exactly to confirm'); return; }
    if (!reason || reason.trim().length < 5) { setError('Provide a reason (min 5 characters)'); return; }
    setLoading(true); setError(null);
    try {
      const d = await adminApiCall(`/admin/customers/${org.id}`, {
        method: 'DELETE',
        body: JSON.stringify({ confirmText, reason }),
      });
      onDeleted(d.message);
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
      <Card style={{ padding: 28, width: 480 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(239,68,68,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Trash2 size={20} style={{ color: '#ef4444' }} />
          </div>
          <div>
            <h3 style={{ margin: 0, color: '#f8fafc', fontSize: 18, fontWeight: 700 }}>Delete Organization</h3>
            <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>This moves the org to the recycle bin. It can be restored.</p>
          </div>
        </div>

        <div style={{ padding: '12px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#f8fafc', marginBottom: 4 }}>{org.name}</div>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>All users will be disabled · Account access removed · HubSpot link preserved</div>
        </div>

        {error && <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', color: '#fca5a5', fontSize: 13, marginBottom: 12 }}>{error}</div>}

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: '#94a3b8', fontWeight: 500 }}>Reason for deletion *</label>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} placeholder="e.g. Customer churned — non-payment after 90 days"
            style={{ ...inputStyle, resize: 'vertical', fontSize: 13 }} />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: '#94a3b8', fontWeight: 500 }}>
            Type <strong style={{ color: '#ef4444' }}>delete</strong> to confirm
          </label>
          <input value={confirmText} onChange={e => setConfirmText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleDelete()}
            placeholder="delete" autoFocus style={inputStyle} />
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={onClose} style={{ flex: 1 }} disabled={loading}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete} disabled={loading || confirmText !== 'delete' || reason.trim().length < 5} style={{ flex: 1 }}>
            {loading ? 'Deleting...' : 'Move to Recycle Bin'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

// =============================================================================
// MERGE ORG MODAL
// Moves all users + data from source (trial) org into target (provisioned) org
// =============================================================================

const MergeOrgModal = ({ sourceOrg, onClose, onMerged }) => {
  const [search, setSearch] = useState('');
  const [allOrgs, setAllOrgs] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [targetOrg, setTargetOrg] = useState(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const fetchOrgs = async () => {
      setLoadingOrgs(true);
      try {
        const d = await adminApiCall('/admin/customers?limit=200&sort=mrr&order=desc');
        setAllOrgs((d.customers || []).filter(o => String(o.id) !== String(sourceOrg.id)));
      } catch (_) {}
      setLoadingOrgs(false);
    };
    fetchOrgs();
  }, [sourceOrg.id]);

  const searchTrimmed = search.trim().toLowerCase();

  // Always show orgs that share the source org's name — most likely merge targets
  const sameNameOrgs = allOrgs.filter(o =>
    o.name?.toLowerCase().trim() === sourceOrg.name?.toLowerCase().trim()
  );

  // Suggested: HubSpot-linked OR active, deduplicated, excluding sameNameOrgs
  const sameNameIds = new Set(sameNameOrgs.map(o => o.id));
  const hubspotLinked = allOrgs.filter(o => o.hubspotCompanyId && !sameNameIds.has(o.id));
  const activeOrgs    = allOrgs.filter(o => o.subscriptionStatus === 'active' && !sameNameIds.has(o.id));
  const seenIds = new Set(hubspotLinked.map(o => o.id));
  const hubspotActive = [
    ...hubspotLinked,
    ...activeOrgs.filter(o => !seenIds.has(o.id)),
  ];

  // When searching, show all orgs regardless of status
  const filtered = searchTrimmed
    ? allOrgs.filter(o => o.name?.toLowerCase().includes(searchTrimmed) || String(o.id).includes(searchTrimmed))
    : [];

  const handleMerge = async () => {
    if (!targetOrg) { setError('Select a target organization'); return; }
    if (!reason.trim()) { setError('Provide a reason for the merge'); return; }
    setLoading(true); setError(null);
    try {
      const d = await adminApiCall(`/admin/customers/${sourceOrg.id}/merge-into/${targetOrg.id}`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      setResult(d);
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 13, boxSizing: 'border-box' };

  const OrgRow = ({ org }) => {
    const isSelected = targetOrg?.id === org.id;
    return (
      <div
        onClick={() => setTargetOrg(org)}
        style={{
          padding: '12px 14px',
          cursor: 'pointer',
          borderBottom: '1px solid #1e293b',
          background: isSelected ? 'rgba(34,197,94,0.12)' : 'transparent',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'background 0.1s',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#f8fafc' }}>{org.name}</span>
            {org.hubspotCompanyId && (
              <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, background: 'rgba(99,102,241,0.2)', color: '#818cf8', fontWeight: 700, flexShrink: 0 }}>
                HUBSPOT
              </span>
            )}
            {org.subscriptionStatus === 'active' && (
              <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, background: 'rgba(34,197,94,0.2)', color: '#22c55e', fontWeight: 700, flexShrink: 0 }}>
                ACTIVE
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#64748b', display: 'flex', gap: 10 }}>
            <span style={{ textTransform: 'capitalize' }}>{org.subscriptionTier}</span>
            <span>·</span>
            <span style={{ textTransform: 'capitalize', color: org.subscriptionStatus === 'active' ? '#22c55e' : '#94a3b8' }}>{org.subscriptionStatus}</span>
            {org.mrr > 0 && <><span>·</span><span style={{ color: '#a78bfa' }}>${(org.mrr / 100).toLocaleString()}/mo</span></>}
            {org.hubspotCompanyId && <><span>·</span><span style={{ fontFamily: 'monospace' }}>HS {org.hubspotCompanyId}</span></>}
          </div>
        </div>
        {isSelected && <CheckCircle size={16} style={{ color: '#22c55e', flexShrink: 0 }} />}
      </div>
    );
  };

  if (result) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
        <Card style={{ padding: 28, width: 480 }}>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <CheckCircle size={48} style={{ color: '#22c55e', marginBottom: 16 }} />
            <h3 style={{ margin: '0 0 8px', color: '#f8fafc' }}>Merge Complete</h3>
            <p style={{ color: '#94a3b8', fontSize: 14, margin: '0 0 16px' }}>{result.message}</p>
            {result.movedUsers?.length > 0 && (
              <div style={{ padding: 12, borderRadius: 8, background: '#0f172a', marginBottom: 16, textAlign: 'left' }}>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6, fontWeight: 600 }}>USERS MOVED</div>
                {result.movedUsers.map(email => (
                  <div key={email} style={{ fontSize: 13, color: '#94a3b8' }}>{email}</div>
                ))}
              </div>
            )}
            <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 20px' }}>A welcome email was sent to each moved user.</p>
            <Button variant="primary" onClick={onMerged}>Done</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
      <Card style={{ padding: 28, width: 580, maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(99,102,241,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <ArrowUpRight size={20} style={{ color: '#818cf8' }} />
          </div>
          <div>
            <h3 style={{ margin: 0, color: '#f8fafc', fontSize: 18, fontWeight: 700 }}>Move into Active Account</h3>
            <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>Move all users and data from the trial org into the provisioned account.</p>
          </div>
        </div>

        {/* Source */}
        <div style={{ padding: '12px 14px', borderRadius: 8, background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: '#818cf8', fontWeight: 700, marginBottom: 3, letterSpacing: '0.05em' }}>TRIAL ORG (will be archived)</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#f8fafc' }}>{sourceOrg.name}</div>
        </div>

        {/* Search — always at the top */}
        <div style={{ marginBottom: 12 }}>
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setTargetOrg(null); }}
            placeholder="Search all orgs by name or ID…"
            style={inputStyle}
            autoFocus
          />
        </div>

        {/* Search results — shown when typing */}
        {searchTrimmed && (
          <div style={{ flex: '0 1 auto', maxHeight: 220, overflowY: 'auto', border: '1px solid #334155', borderRadius: 8, marginBottom: 12 }}>
            {loadingOrgs
              ? <div style={{ padding: 20, color: '#64748b', fontSize: 13, textAlign: 'center' }}>Loading…</div>
              : filtered.length === 0
                ? <div style={{ padding: 20, color: '#64748b', fontSize: 13, textAlign: 'center' }}>No organizations found</div>
                : filtered.slice(0, 50).map(org => <OrgRow key={org.id} org={org} />)
            }
          </div>
        )}

        {/* Same company name — most likely targets, shown when not actively searching */}
        {!searchTrimmed && sameNameOrgs.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: '#f59e0b', fontWeight: 700, marginBottom: 6, letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f59e0b', display: 'inline-block' }} />
              SAME COMPANY NAME — PICK THE CORRECT ONE
            </div>
            <div style={{ border: '1px solid rgba(245,158,11,0.35)', borderRadius: 8, marginBottom: 4, overflow: 'hidden' }}>
              {sameNameOrgs.map(org => <OrgRow key={org.id} org={org} />)}
            </div>
          </div>
        )}

        {/* HubSpot-linked & active accounts */}
        {!searchTrimmed && hubspotActive.length > 0 && (
          <div style={{ marginBottom: 4 }}>
            <div style={{ fontSize: 11, color: '#22c55e', fontWeight: 700, marginBottom: 6, letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
              HUBSPOT-LINKED &amp; ACTIVE ACCOUNTS
            </div>
            <div style={{ border: '1px solid rgba(34,197,94,0.3)', borderRadius: 8, marginBottom: 12, overflow: 'hidden', maxHeight: 220, overflowY: 'auto' }}>
              {hubspotActive.map(org => <OrgRow key={org.id} org={org} />)}
            </div>
          </div>
        )}

        {/* Selected target confirmation */}
        {targetOrg && (
          <div style={{ padding: '12px 14px', borderRadius: 8, background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)', marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: '#22c55e', fontWeight: 700, marginBottom: 3, letterSpacing: '0.05em' }}>TARGET (users will land here)</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#f8fafc' }}>{targetOrg.name}</div>
            {targetOrg.hubspotCompanyId && (
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>HubSpot ID: {targetOrg.hubspotCompanyId}</div>
            )}
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: '#94a3b8', fontWeight: 500 }}>Reason for merge *</label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={2}
            placeholder="e.g. Trial user signed agreement — placing under provisioned org"
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        {error && <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', color: '#fca5a5', fontSize: 13, marginBottom: 12 }}>{error}</div>}

        <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', fontSize: 12, color: '#fbbf24', marginBottom: 16 }}>
          ⚠ All users in <strong>{sourceOrg.name}</strong> will be moved to the target org. Sessions will be revoked — they must log in again. A welcome email will be sent to each user. The trial org will be archived.
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={onClose} style={{ flex: 1 }} disabled={loading}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleMerge}
            disabled={loading || !targetOrg || !reason.trim()}
            style={{ flex: 1, background: targetOrg ? 'linear-gradient(135deg, #16a34a, #15803d)' : undefined }}
          >
            {loading ? 'Moving users…' : `Move into ${targetOrg ? targetOrg.name : '…'}`}
          </Button>
        </div>
      </Card>
    </div>
  );
};

// =============================================================================
// HUBSPOT DUPLICATES PAGE
// Detects orgs with matching names but different HubSpot company IDs,
// and allows re-linking an org to the correct HubSpot company.
// =============================================================================

const HubSpotDuplicatesPage = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relinking, setRelinking] = useState(null);
  const [relinkInput, setRelinkInput] = useState({});
  const [relinkMsg, setRelinkMsg] = useState({});

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const d = await adminApiCall('/admin/hubspot/duplicates');
      setGroups(d.duplicateGroups || []);
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleRelink = async (orgId) => {
    const newId = (relinkInput[orgId] || '').trim();
    if (!newId) return;
    setRelinking(orgId);
    try {
      const d = await adminApiCall(`/admin/customers/${orgId}/relink-hubspot`, {
        method: 'POST',
        body: JSON.stringify({ hubspotCompanyId: newId }),
      });
      setRelinkMsg(prev => ({ ...prev, [orgId]: { type: 'success', text: d.message } }));
      load();
    } catch (err) {
      setRelinkMsg(prev => ({ ...prev, [orgId]: { type: 'error', text: err.message } }));
    }
    setRelinking(null);
  };

  const statusColor = s => s === 'active' ? '#22c55e' : s === 'past_due' ? '#f59e0b' : '#64748b';

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: '0 0 8px', fontSize: 28, fontWeight: 800, color: '#f8fafc' }}>HubSpot Duplicates</h1>
        <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}>
          Organizations that share a name but have different HubSpot company IDs. Re-link to the correct ID to consolidate.
        </p>
      </div>

      {loading && <div style={{ color: '#64748b', textAlign: 'center', padding: 60 }}>Scanning for duplicates…</div>}
      {error && <div style={{ color: '#ef4444', padding: 20 }}>{error}</div>}

      {!loading && groups.length === 0 && (
        <Card style={{ padding: 40, textAlign: 'center' }}>
          <CheckCircle size={40} style={{ color: '#22c55e', marginBottom: 12 }} />
          <div style={{ color: '#f8fafc', fontWeight: 700, fontSize: 18, marginBottom: 8 }}>No duplicates found</div>
          <div style={{ color: '#64748b', fontSize: 14 }}>All linked organizations have unique HubSpot company IDs.</div>
          <Button variant="secondary" icon={RefreshCw} onClick={load} style={{ marginTop: 20 }}>Re-scan</Button>
        </Card>
      )}

      {groups.map(group => (
        <Card key={group.name} style={{ padding: 24, marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <h3 style={{ margin: 0, color: '#f8fafc', fontSize: 16, fontWeight: 700 }}>{group.name}</h3>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{group.orgs.length} matching organizations</div>
            </div>
            <div style={{ padding: '4px 12px', borderRadius: 20, background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', fontSize: 12, color: '#fbbf24', fontWeight: 600 }}>
              DUPLICATE
            </div>
          </div>

          {group.orgs.map(org => (
            <div key={org.id} style={{ padding: 16, borderRadius: 10, background: '#0f172a', marginBottom: 12, border: '1px solid #1e293b' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>ORG ID</div>
                  <div style={{ fontSize: 13, color: '#94a3b8', fontFamily: 'monospace' }}>{org.id}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>STATUS</div>
                  <div style={{ fontSize: 13, color: statusColor(org.subscriptionStatus), fontWeight: 600, textTransform: 'capitalize' }}>{org.subscriptionStatus}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>ACTIVE USERS</div>
                  <div style={{ fontSize: 13, color: '#94a3b8' }}>{org.activeUserCount}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>HUBSPOT COMPANY ID</div>
                  <div style={{ fontSize: 13, color: '#818cf8', fontFamily: 'monospace' }}>{org.hubspotCompanyId || '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>MRR</div>
                  <div style={{ fontSize: 13, color: '#94a3b8' }}>{org.mrr ? `$${(org.mrr / 100).toLocaleString()}` : '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>CREATED</div>
                  <div style={{ fontSize: 13, color: '#94a3b8' }}>{org.createdAt ? new Date(org.createdAt).toLocaleDateString() : '—'}</div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  value={relinkInput[org.id] || ''}
                  onChange={e => setRelinkInput(p => ({ ...p, [org.id]: e.target.value }))}
                  placeholder="New HubSpot Company ID"
                  style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid #334155', background: '#1e293b', color: '#f8fafc', fontSize: 13 }}
                />
                <Button
                  variant="secondary"
                  onClick={() => handleRelink(org.id)}
                  disabled={relinking === org.id || !(relinkInput[org.id] || '').trim()}
                >
                  {relinking === org.id ? 'Saving…' : 'Re-link'}
                </Button>
              </div>
              {relinkMsg[org.id] && (
                <div style={{ marginTop: 8, fontSize: 12, color: relinkMsg[org.id].type === 'success' ? '#22c55e' : '#ef4444' }}>
                  {relinkMsg[org.id].text}
                </div>
              )}
            </div>
          ))}
        </Card>
      ))}
    </div>
  );
};

// =============================================================================
// =============================================================================
// HUBSPOT SUBSCRIPTIONS PAGE
// Pulls live subscription objects from HubSpot Commerce Hub with company + contact,
// cross-references against kNine orgs, and lets you link in one click.
// =============================================================================

const HubSpotSubscriptionsPage = () => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linking, setLinking] = useState(null); // subId being linked
  const [linkTarget, setLinkTarget] = useState({}); // subId -> orgId input
  const [linkMsg, setLinkMsg] = useState({});
  const [allOrgs, setAllOrgs] = useState([]);

  useEffect(() => {
    load();
    loadOrgs();
  }, []);

  const [scopeError, setScopeError] = React.useState(false);

  const load = async () => {
    setLoading(true); setError(null); setScopeError(false);
    try {
      const d = await adminApiCall('/admin/hubspot/subscriptions');
      if (d.scopeError) { setScopeError(true); setError(d.error); }
      else if (d.error) setError(d.error);
      setSubs(d.subscriptions || []);
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const loadOrgs = async () => {
    try {
      const d = await adminApiCall('/admin/customers?limit=200&sort=name&order=asc');
      setAllOrgs(d.customers || []);
    } catch (_) {}
  };

  const handleLink = async (sub) => {
    const orgId = linkTarget[sub.id];
    if (!orgId || !sub.company?.id) return;
    setLinking(sub.id);
    try {
      await adminApiCall(`/admin/customers/${orgId}/relink-hubspot`, {
        method: 'POST',
        body: JSON.stringify({ hubspotCompanyId: sub.company.id }),
      });
      setLinkMsg(p => ({ ...p, [sub.id]: { type: 'success', text: 'Linked — syncing deal data…' } }));
      // Trigger a sync for just this org
      await adminApiCall('/admin/hubspot/sync', { method: 'POST' }).catch(() => {});
      setTimeout(() => load(), 1500);
    } catch (err) {
      setLinkMsg(p => ({ ...p, [sub.id]: { type: 'error', text: err.message } }));
    }
    setLinking(null);
  };

  const fmtMoney = (v) => v > 0 ? '$' + parseFloat(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
  const fmtDate  = (d) => d ? new Date(d).toLocaleDateString() : '—';
  const statusColor = { active: '#22c55e', canceled: '#ef4444', paused: '#f59e0b', past_due: '#f59e0b' };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: '0 0 6px', fontSize: 28, fontWeight: 800, color: '#f8fafc' }}>HubSpot Subscriptions</h1>
        <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}>
          Live from HubSpot Commerce Hub · shows every subscription with its company and contact, and whether it's linked to a kNine org.
        </p>
      </div>

      {loading && <LoadingSpinner text="Fetching subscriptions from HubSpot…" />}
      {error && scopeError && (
        <Card style={{ padding: 24, border: '1px solid rgba(245,158,11,0.4)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <AlertTriangle size={22} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{ color: '#fbbf24', fontWeight: 700, fontSize: 15, marginBottom: 8 }}>Missing HubSpot App Scopes</div>
              <p style={{ color: '#94a3b8', fontSize: 14, margin: '0 0 16px', lineHeight: 1.6 }}>
                Your HubSpot private app token doesn't have permission to read Commerce Hub subscriptions. You need to add these two scopes:
              </p>
              <div style={{ background: '#0f172a', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontFamily: 'monospace', fontSize: 13, color: '#818cf8' }}>
                crm.objects.subscriptions.read<br />
                crm.schemas.subscriptions.read
              </div>
              <div style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.7, marginBottom: 16 }}>
                <strong style={{ color: '#f8fafc' }}>To fix:</strong><br />
                1. Go to HubSpot → <strong style={{ color: '#f8fafc' }}>Settings</strong> → <strong style={{ color: '#f8fafc' }}>Integrations</strong> → <strong style={{ color: '#f8fafc' }}>Private Apps</strong><br />
                2. Click your kNine app → <strong style={{ color: '#f8fafc' }}>Scopes</strong> tab<br />
                3. Search for "subscriptions" and enable both read scopes above<br />
                4. Click <strong style={{ color: '#f8fafc' }}>Update app</strong> — a new token will be generated<br />
                5. Update <strong style={{ color: '#f8fafc' }}>HUBSPOT_ACCESS_TOKEN</strong> in your environment variables with the new token
              </div>
              <Button variant="secondary" icon={RefreshCw} onClick={load}>Retry after updating</Button>
            </div>
          </div>
        </Card>
      )}
      {error && !scopeError && (
        <Card style={{ padding: 20 }}>
          <div style={{ color: '#ef4444', fontSize: 14, marginBottom: 8 }}>⚠ HubSpot API error</div>
          <div style={{ color: '#94a3b8', fontSize: 13 }}>{error}</div>
          <Button variant="secondary" icon={RefreshCw} onClick={load} style={{ marginTop: 16 }}>Retry</Button>
        </Card>
      )}

      {!loading && !error && subs.length === 0 && (
        <Card style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ color: '#64748b', fontSize: 14 }}>No subscriptions found in HubSpot. Make sure HUBSPOT_ACCESS_TOKEN is set and your account has Commerce Hub.</div>
          <Button variant="secondary" icon={RefreshCw} onClick={load} style={{ marginTop: 16 }}>Retry</Button>
        </Card>
      )}

      {!loading && subs.map(sub => {
        const isLinked = !!sub.knineOrg;
        const msg = linkMsg[sub.id];
        return (
          <Card key={sub.id} style={{ padding: 20, marginBottom: 16, border: isLinked ? '1px solid rgba(34,197,94,0.2)' : '1px solid #334155' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 20 }}>

              {/* Left: subscription + company info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#f8fafc' }}>{sub.name}</span>
                  <span style={{
                    fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 700,
                    background: `${statusColor[sub.status] || '#64748b'}22`,
                    color: statusColor[sub.status] || '#64748b',
                    textTransform: 'uppercase',
                  }}>{sub.status || 'unknown'}</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 12 }}>
                  {[
                    { label: 'Amount', value: fmtMoney(sub.amount), color: '#10b981' },
                    { label: 'MRR', value: sub.mrr > 0 ? fmtMoney(sub.mrr) : (sub.amount > 0 ? fmtMoney(sub.amount) : '—'), color: '#a78bfa' },
                    { label: 'Next Payment', value: fmtDate(sub.nextPaymentDate) },
                    { label: 'Billing Period', value: sub.billingPeriod || '—' },
                  ].map(f => (
                    <div key={f.label} style={{ padding: '8px 10px', background: '#0f172a', borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{f.label}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: f.color || '#f8fafc' }}>{f.value}</div>
                    </div>
                  ))}
                </div>

                {/* Company */}
                {sub.company && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <Building2 size={13} style={{ color: '#64748b', flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: '#f8fafc', fontWeight: 600 }}>{sub.company.name}</span>
                    <span style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>ID: {sub.company.id}</span>
                    {sub.company.domain && <span style={{ fontSize: 11, color: '#64748b' }}>· {sub.company.domain}</span>}
                  </div>
                )}
                {/* Contact */}
                {sub.contact?.email && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Mail size={13} style={{ color: '#64748b', flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: '#94a3b8' }}>{sub.contact.email}</span>
                    {sub.contact.name && <span style={{ fontSize: 13, color: '#64748b' }}>· {sub.contact.name}</span>}
                  </div>
                )}
              </div>

              {/* Right: kNine link status */}
              <div style={{ minWidth: 260, flexShrink: 0 }}>
                {isLinked ? (
                  <div style={{ padding: '12px 14px', borderRadius: 10, background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)' }}>
                    <div style={{ fontSize: 11, color: '#22c55e', fontWeight: 700, marginBottom: 4 }}>LINKED TO KNINE</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#f8fafc' }}>{sub.knineOrg.name}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, textTransform: 'capitalize' }}>{sub.knineOrg.tier} · {sub.knineOrg.status}</div>
                  </div>
                ) : (
                  <div style={{ padding: '12px 14px', borderRadius: 10, background: '#0f172a', border: '1px solid #475569' }}>
                    <div style={{ fontSize: 11, color: '#f59e0b', fontWeight: 700, marginBottom: 8 }}>NOT LINKED TO KNINE</div>
                    <select
                      value={linkTarget[sub.id] || ''}
                      onChange={e => setLinkTarget(p => ({ ...p, [sub.id]: e.target.value }))}
                      style={{ width: '100%', padding: '7px 10px', borderRadius: 7, border: '1px solid #334155', background: '#1e293b', color: '#f8fafc', fontSize: 12, marginBottom: 8 }}
                    >
                      <option value="">— select kNine org —</option>
                      {allOrgs.map(o => (
                        <option key={o.id} value={o.id}>{o.name} ({o.plan || o.subscriptionTier || 'trial'})</option>
                      ))}
                    </select>
                    <Button
                      variant="primary"
                      onClick={() => handleLink(sub)}
                      disabled={!linkTarget[sub.id] || linking === sub.id}
                      style={{ width: '100%', justifyContent: 'center', background: 'linear-gradient(135deg, #4f46e5, #6366f1)' }}
                    >
                      {linking === sub.id ? 'Linking…' : 'Link & Sync'}
                    </Button>
                  </div>
                )}
                {msg && (
                  <div style={{ marginTop: 8, fontSize: 12, color: msg.type === 'success' ? '#22c55e' : '#ef4444' }}>{msg.text}</div>
                )}
              </div>
            </div>
          </Card>
        );
      })}

      {!loading && subs.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          <div style={{ fontSize: 13, color: '#64748b' }}>
            {subs.filter(s => s.knineOrg).length} of {subs.length} subscription{subs.length !== 1 ? 's' : ''} linked to kNine orgs
          </div>
          <Button variant="secondary" icon={RefreshCw} onClick={load} disabled={loading}>Refresh</Button>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// COMPANY CONSOLIDATION PAGE
// Search HubSpot for companies by name, pick a primary, merge duplicates in
// HubSpot AND in kNine in one workflow.
// =============================================================================

const ConsolidatePage = () => {
  const [query, setQuery] = React.useState('');
  const [searching, setSearching] = React.useState(false);
  const [companies, setCompanies] = React.useState([]);
  const [searchError, setSearchError] = React.useState(null);
  const [primaryId, setPrimaryId] = React.useState(null);
  const [consolidating, setConsolidating] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [mergeKnineOrgs, setMergeKnineOrgs] = React.useState(true);
  const [showConfirm, setShowConfirm] = React.useState(false);

  const search = async () => {
    if (query.trim().length < 2) return;
    setSearching(true); setSearchError(null); setCompanies([]); setPrimaryId(null); setResult(null);
    try {
      const d = await adminApiCall(`/admin/hubspot/companies/search-full?q=${encodeURIComponent(query.trim())}`);
      setCompanies(d.companies || []);
      if ((d.companies || []).length === 0) setSearchError('No companies found in HubSpot for that name.');
      // Auto-select primary: prefer company with active deal/subscription or oldest
      const best = (d.companies || []).find(c => c.hasActiveDeal || c.hasSubscription) || (d.companies || [])[0];
      if (best) setPrimaryId(best.id);
    } catch (err) { setSearchError(err.message); }
    setSearching(false);
  };

  const secondaryIds = companies.filter(c => c.id !== primaryId).map(c => c.id);

  const handleConsolidate = async () => {
    setShowConfirm(false);
    setConsolidating(true); setResult(null);
    try {
      const d = await adminApiCall('/admin/hubspot/companies/consolidate', {
        method: 'POST',
        body: JSON.stringify({ primaryHubspotId: primaryId, secondaryHubspotIds: secondaryIds, mergeKnineOrgs }),
      });
      setResult(d);
      // Refresh company cards to show updated state
      await search();
    } catch (err) { setResult({ success: false, summary: err.message, errors: [{ message: err.message }] }); }
    setConsolidating(false);
  };

  const primary = companies.find(c => c.id === primaryId);

  const tierColor = t => t === 'trial' || t === 'free' ? '#f59e0b' : '#22c55e';
  const statusBadge = (status, tier) => {
    const color = status === 'active' && tier !== 'trial' ? '#22c55e' : status === 'active' ? '#f59e0b' : '#94a3b8';
    const label = status === 'active' && tier !== 'trial' ? 'PAYING' : status === 'active' ? 'TRIAL' : (status || 'UNKNOWN').toUpperCase();
    return <span style={{ fontSize: 10, fontWeight: 700, color, background: `${color}22`, border: `1px solid ${color}44`, padding: '2px 8px', borderRadius: 20 }}>{label}</span>;
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: '0 0 8px', fontSize: 28, fontWeight: 800, color: '#f8fafc' }}>Company Consolidation</h1>
        <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}>
          Search for a company in HubSpot, select the primary account, and merge all duplicates into it — in HubSpot and kNine at once.
        </p>
      </div>

      {/* Search */}
      <Card style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && search()}
            placeholder="Search HubSpot companies by name…"
            style={{ flex: 1, background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: '10px 14px', color: '#f8fafc', fontSize: 14, outline: 'none' }}
          />
          <Button variant="primary" onClick={search} loading={searching} icon={Search}>Search</Button>
        </div>
        {searchError && <div style={{ marginTop: 10, color: '#f87171', fontSize: 13 }}>{searchError}</div>}
      </Card>

      {/* Results */}
      {companies.length > 0 && (
        <>
          <div style={{ marginBottom: 12, color: '#94a3b8', fontSize: 13 }}>
            {companies.length} companies found. Click <strong style={{ color: '#f8fafc' }}>Set as Primary</strong> on the account that should receive all others.
          </div>

          {companies.map(company => {
            const isPrimary = company.id === primaryId;
            const borderColor = isPrimary ? '#22c55e' : '#334155';
            return (
              <Card key={company.id} style={{ padding: 20, marginBottom: 12, border: `2px solid ${borderColor}`, position: 'relative', transition: 'border-color 0.15s' }}>
                {isPrimary && (
                  <div style={{ position: 'absolute', top: 12, right: 12, background: '#22c55e', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>
                    PRIMARY
                  </div>
                )}
                {!isPrimary && companies.length > 1 && (
                  <div style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(239,68,68,0.15)', color: '#f87171', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, border: '1px solid rgba(239,68,68,0.3)' }}>
                    WILL MERGE IN
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  {/* Left: HubSpot info */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      <Building2 size={18} style={{ color: '#818cf8', flexShrink: 0 }} />
                      <div>
                        <div style={{ color: '#f8fafc', fontWeight: 700, fontSize: 15 }}>{company.name}</div>
                        {company.domain && <div style={{ color: '#64748b', fontSize: 12 }}>{company.domain}</div>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>HS ID: <span style={{ color: '#818cf8', fontFamily: 'monospace' }}>{company.id}</span></span>
                      {company.contactCount > 0 && <span style={{ fontSize: 11, color: '#94a3b8' }}>👤 {company.contactCount} contact{company.contactCount !== 1 ? 's' : ''}</span>}
                      {company.dealCount > 0 && <span style={{ fontSize: 11, color: '#94a3b8' }}>💼 {company.dealCount} deal{company.dealCount !== 1 ? 's' : ''}</span>}
                    </div>
                    {company.deals.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        {company.deals.map(dl => (
                          <div key={dl.id} style={{ fontSize: 12, color: dl.closedWon ? '#22c55e' : '#94a3b8', marginBottom: 2 }}>
                            {dl.closedWon ? '✅' : '○'} {dl.name || 'Untitled deal'}{dl.amount ? ` — $${dl.amount.toLocaleString()}` : ''}
                          </div>
                        ))}
                      </div>
                    )}
                    {company.hasSubscription && (
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#818cf8', background: 'rgba(129,140,248,0.1)', border: '1px solid rgba(129,140,248,0.3)', padding: '2px 8px', borderRadius: 20 }}>SUBSCRIPTION</span>
                    )}
                  </div>

                  {/* Right: kNine org */}
                  <div style={{ borderLeft: '1px solid #1e293b', paddingLeft: 16 }}>
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>kNine Org</div>
                    {company.knineOrg ? (
                      <div>
                        <div style={{ color: '#f8fafc', fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{company.knineOrg.name}</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {statusBadge(company.knineOrg.subscriptionStatus, company.knineOrg.subscriptionTier)}
                          <span style={{ fontSize: 11, color: '#64748b' }}>{company.knineOrg.subscriptionTier}</span>
                        </div>
                      </div>
                    ) : (
                      <div style={{ color: '#475569', fontSize: 13, fontStyle: 'italic' }}>No kNine org linked</div>
                    )}
                  </div>
                </div>

                {!isPrimary && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #1e293b', display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="secondary" onClick={() => setPrimaryId(company.id)} style={{ fontSize: 12, padding: '6px 14px' }}>
                      Set as Primary
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}

          {/* Options + Consolidate */}
          {companies.length > 1 && !result && (
            <Card style={{ padding: 20, marginTop: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#94a3b8', fontSize: 14 }}>
                  <input type="checkbox" checked={mergeKnineOrgs} onChange={e => setMergeKnineOrgs(e.target.checked)}
                    style={{ width: 16, height: 16, cursor: 'pointer' }} />
                  Also merge kNine orgs into the primary's linked org
                </label>
              </div>
              <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#fbbf24' }}>
                <strong>This will:</strong> merge {secondaryIds.length} HubSpot compan{secondaryIds.length === 1 ? 'y' : 'ies'} into <strong>{primary?.name}</strong>
                {mergeKnineOrgs && ' and move all users from their kNine orgs into the primary\'s kNine org'}.
                This action is <strong>not reversible in HubSpot</strong>.
              </div>
              <Button
                variant="primary"
                icon={GitMerge}
                onClick={() => setShowConfirm(true)}
                loading={consolidating}
                style={{ background: '#dc2626' }}
              >
                Consolidate {secondaryIds.length} duplicate{secondaryIds.length !== 1 ? 's' : ''} into {primary?.name}
              </Button>
            </Card>
          )}
        </>
      )}

      {/* Result */}
      {result && (
        <Card style={{ padding: 20, marginTop: 16, border: `1px solid ${result.success ? '#22c55e' : '#f87171'}44` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            {result.success
              ? <CheckCircle size={20} style={{ color: '#22c55e' }} />
              : <AlertTriangle size={20} style={{ color: '#f87171' }} />}
            <div style={{ color: result.success ? '#22c55e' : '#f87171', fontWeight: 700 }}>{result.summary}</div>
          </div>
          {result.knineMerges?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>kNine Orgs Merged</div>
              {result.knineMerges.map(m => (
                <div key={m.sourceOrgId} style={{ fontSize: 13, color: '#94a3b8', marginBottom: 4 }}>
                  ✓ <span style={{ color: '#f8fafc' }}>{m.sourceOrgName}</span> → {m.targetOrgName} ({m.movedUserCount} user{m.movedUserCount !== 1 ? 's' : ''} moved)
                </div>
              ))}
            </div>
          )}
          {result.errors?.length > 0 && (
            <div>
              <div style={{ fontSize: 12, color: '#f87171', marginBottom: 6, textTransform: 'uppercase' }}>Errors</div>
              {result.errors.map((e, i) => (
                <div key={i} style={{ fontSize: 12, color: '#f87171', marginBottom: 4 }}>⚠ {e.step ? `[${e.step}] ` : ''}{e.message}</div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Confirm modal */}
      {showConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <Card style={{ padding: 32, maxWidth: 480, width: '90%' }}>
            <h3 style={{ margin: '0 0 12px', color: '#f8fafc', fontSize: 18, fontWeight: 700 }}>Confirm Consolidation</h3>
            <p style={{ color: '#94a3b8', fontSize: 14, marginBottom: 20 }}>
              You are about to merge <strong style={{ color: '#f8fafc' }}>{secondaryIds.length} HubSpot compan{secondaryIds.length === 1 ? 'y' : 'ies'}</strong> into <strong style={{ color: '#f8fafc' }}>{primary?.name}</strong>.
              All contacts, deals, and notes from the duplicates will be moved to the primary.
              {mergeKnineOrgs && ' All users in the duplicate kNine orgs will also be moved into the primary\'s kNine org.'}
              {' '}<strong style={{ color: '#f87171' }}>This cannot be undone in HubSpot.</strong>
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setShowConfirm(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleConsolidate} style={{ background: '#dc2626' }} icon={GitMerge}>Consolidate Now</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// RECYCLE BIN PAGE
// =============================================================================

const RecycleBinPage = () => {
  const [deleted, setDeleted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => { loadBin(); }, []);

  const loadBin = async () => {
    setLoading(true);
    try {
      const d = await adminApiCall('/admin/recycle-bin');
      setDeleted(d.deleted || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleRestore = async (org) => {
    if (!window.confirm(`Restore "${org.name}"? Users will be re-enabled and org set to active.`)) return;
    setRestoring(org.id);
    try {
      const d = await adminApiCall(`/admin/customers/${org.id}/restore`, { method: 'POST' });
      setMessage(d.message);
      loadBin();
    } catch (err) { alert('Restore failed: ' + err.message); }
    setRestoring(null);
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleString() : '—';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: '0 0 4px', fontSize: 28, fontWeight: 800, color: '#f8fafc' }}>Recycle Bin</h1>
          <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>Deleted organizations — restore to re-enable access</p>
        </div>
        <Button variant="secondary" icon={RefreshCw} onClick={loadBin} disabled={loading}>Refresh</Button>
      </div>

      {message && (
        <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', color: '#6ee7b7', fontSize: 13, marginBottom: 16 }}>
          {message}
        </div>
      )}

      {loading ? <LoadingSpinner text="Loading recycle bin..." /> : deleted.length === 0 ? (
        <Card style={{ padding: 48, textAlign: 'center' }}>
          <Trash2 size={40} style={{ color: '#334155', marginBottom: 12 }} />
          <p style={{ color: '#64748b', margin: 0 }}>Recycle bin is empty</p>
        </Card>
      ) : (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#0f172a' }}>
                {['Organization', 'Plan', 'Deleted', 'Deleted By', 'Reason', 'Users', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {deleted.map((org, i) => (
                <tr key={org.id} style={{ borderTop: i === 0 ? 'none' : '1px solid #1e293b' }}>
                  <td style={{ padding: '12px 14px' }}>
                    <div style={{ fontWeight: 600, color: '#f8fafc', fontSize: 14 }}>{org.name}</div>
                    <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#475569' }}>{org.id}</div>
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    <Badge variant="default" style={{ textTransform: 'capitalize', fontSize: 11 }}>{org.subscriptionTier || 'free'}</Badge>
                  </td>
                  <td style={{ padding: '12px 14px', fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>{fmtDate(org.deleted_at)}</td>
                  <td style={{ padding: '12px 14px', fontSize: 13 }}>
                    <div style={{ color: '#f8fafc' }}>{org.deleted_by_name || '—'}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{org.deleted_by_email || ''}</div>
                  </td>
                  <td style={{ padding: '12px 14px', fontSize: 12, color: '#94a3b8', maxWidth: 200 }}>{org.deleted_reason || '—'}</td>
                  <td style={{ padding: '12px 14px', fontSize: 13, color: '#f8fafc' }}>{org.user_count || 0}</td>
                  <td style={{ padding: '12px 14px' }}>
                    <Button variant="success" size="sm" onClick={() => handleRestore(org)} disabled={restoring === org.id}>
                      {restoring === org.id ? 'Restoring...' : 'Restore'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

// =============================================================================
// MAIN ADMIN APP
// =============================================================================

const AdminLayout = ({ children }) => {
  const { admin, logout } = useAdmin();
  const [currentPage, setCurrentPage] = useState('dashboard');

  const navItems = [
    { id: 'dashboard',        label: 'Dashboard',       icon: BarChart3 },
    { id: 'accounts',         label: 'Accounts',        icon: DollarSign },
    { id: 'customers',        label: 'Customers',       icon: Building2 },
    { id: 'hubspotSubs',      label: 'HS Subscriptions', icon: DollarSign },
    { id: 'consolidate',      label: 'Consolidate',      icon: GitMerge },
    { id: 'hubspotDuplicates',label: 'HS Duplicates',   icon: AlertTriangle },
    { id: 'audit',            label: 'Audit Logs',      icon: History },
    { id: 'admins',           label: 'Admin Users',     icon: Shield },
    { id: 'recycleBin',       label: 'Recycle Bin',     icon: Trash2 },
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#0f172a' }}>
      {/* Sidebar */}
      <div style={{ width: 260, background: '#1e293b', borderRight: '1px solid #334155', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: 20, borderBottom: '1px solid #334155' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #0a66c2, #10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Shield size={20} style={{ color: '#fff' }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, color: '#f8fafc' }}>kNine Admin</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>Internal Portal</div>
            </div>
          </div>
        </div>

        <nav style={{ flex: 1, padding: 12 }}>
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => setCurrentPage(item.id)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                borderRadius: 10,
                border: 'none',
                background: currentPage === item.id ? 'rgba(10, 102, 194, 0.2)' : 'transparent',
                color: currentPage === item.id ? '#0a66c2' : '#94a3b8',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
                marginBottom: 4,
                textAlign: 'left'
              }}
            >
              <item.icon size={18} />
              {item.label}
            </button>
          ))}
        </nav>

        <div style={{ padding: 16, borderTop: '1px solid #334155' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f8fafc', fontWeight: 600 }}>
              {admin?.name?.charAt(0) || 'A'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: '#f8fafc', fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{admin?.name}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>{admin?.email}</div>
            </div>
          </div>
          <Button variant="ghost" icon={Key} onClick={() => setCurrentPage('changePassword')} style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 4 }}>Change Password</Button>
          <Button variant="ghost" icon={Shield} onClick={() => setCurrentPage('twoFactor')} style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 4 }}>Two-Factor Auth</Button>
          <Button variant="ghost" icon={LogOut} onClick={logout} style={{ width: '100%', justifyContent: 'flex-start' }}>Sign Out</Button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <div style={{ padding: 32, maxWidth: 1400, margin: '0 auto' }}>
          {children({ currentPage, setCurrentPage })}
        </div>
      </div>
    </div>
  );
};

const TwoFactorPage = ({ onBack }) => {
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState('loading'); // loading | status | setup | verify | done | disable
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const inputStyle = { width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 14, boxSizing: 'border-box' };

  useEffect(() => {
    adminApiCall('/admin/2fa/status')
      .then(d => { setStatus(d.enabled); setStep(d.enabled ? 'status' : 'status'); })
      .catch(() => setStep('status'));
  }, []);

  const startSetup = async () => {
    setLoading(true); setError(null);
    try {
      const d = await adminApiCall('/admin/2fa/setup', { method: 'POST' });
      setQrCode(d.qrCodeUrl); setSecret(d.secret); setStep('setup');
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const verifyEnable = async () => {
    if (code.length !== 6) { setError('Enter a 6-digit code'); return; }
    setLoading(true); setError(null);
    try {
      await adminApiCall('/admin/2fa/enable', { method: 'POST', body: JSON.stringify({ code }) });
      setStatus(true); setStep('done'); setMessage('2FA is now active on your account.');
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const disableHandler = async () => {
    if (!password) { setError('Enter your password'); return; }
    setLoading(true); setError(null);
    try {
      await adminApiCall('/admin/2fa/disable', { method: 'POST', body: JSON.stringify({ password }) });
      setStatus(false); setStep('status'); setMessage('2FA has been disabled.'); setPassword('');
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  return (
    <div style={{ maxWidth: 480 }}>
      <Button variant="ghost" icon={ChevronLeft} onClick={onBack} style={{ marginBottom: 20 }}>Back</Button>
      <Card style={{ padding: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(10,102,194,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Shield size={22} style={{ color: '#0a66c2' }} />
          </div>
          <div>
            <h2 style={{ margin: 0, color: '#f8fafc', fontSize: 18, fontWeight: 700 }}>Two-Factor Authentication</h2>
            <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>TOTP via Google Authenticator or Authy</p>
          </div>
        </div>

        {message && <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', color: '#6ee7b7', fontSize: 13, marginBottom: 16 }}>{message}</div>}
        {error && <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', fontSize: 13, marginBottom: 16 }}>{error}</div>}

        {(step === 'status' || step === 'loading') && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderRadius: 10, background: '#0f172a', marginBottom: 20 }}>
              <div>
                <div style={{ fontWeight: 600, color: '#f8fafc', fontSize: 14 }}>2FA Status</div>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{status ? 'Enabled and protecting your account' : 'Not enabled — your account is less secure'}</div>
              </div>
              <Badge variant={status ? 'success' : 'danger'}>{status ? 'Enabled' : 'Disabled'}</Badge>
            </div>
            {status ? (
              <Button variant="danger" onClick={() => { setStep('disable'); setError(null); }} style={{ width: '100%' }}>Disable 2FA</Button>
            ) : (
              <Button variant="primary" icon={Shield} onClick={startSetup} disabled={loading} style={{ width: '100%' }}>
                {loading ? 'Generating...' : 'Set Up 2FA'}
              </Button>
            )}
          </div>
        )}

        {step === 'setup' && qrCode && (
          <div>
            <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 16 }}>
              Scan this QR code with your authenticator app, then enter the 6-digit code to confirm.
            </p>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <img src={qrCode} alt="2FA QR Code" style={{ width: 200, height: 200, borderRadius: 12, background: '#fff', padding: 8 }} />
            </div>
            <div style={{ padding: '10px 14px', borderRadius: 8, background: '#0f172a', marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Manual entry key</div>
              <div style={{ fontFamily: 'monospace', fontSize: 13, color: '#93c5fd', wordBreak: 'break-all', letterSpacing: 2 }}>{secret}</div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: '#94a3b8' }}>Enter 6-digit verification code</label>
              <input
                type="text" inputMode="numeric" maxLength={6} value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={e => e.key === 'Enter' && verifyEnable()}
                placeholder="123456" autoFocus
                style={{ ...inputStyle, fontSize: 22, letterSpacing: 8, textAlign: 'center' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Button variant="secondary" onClick={() => { setStep('status'); setError(null); }} style={{ flex: 1 }}>Cancel</Button>
              <Button variant="primary" onClick={verifyEnable} disabled={loading || code.length !== 6} style={{ flex: 1 }}>
                {loading ? 'Verifying...' : 'Activate 2FA'}
              </Button>
            </div>
          </div>
        )}

        {step === 'done' && (
          <div style={{ textAlign: 'center' }}>
            <CheckCircle size={48} style={{ color: '#10b981', marginBottom: 12 }} />
            <p style={{ color: '#94a3b8', fontSize: 13 }}>Your account is now protected with two-factor authentication.</p>
            <Button variant="secondary" onClick={() => setStep('status')} style={{ marginTop: 8 }}>Done</Button>
          </div>
        )}

        {step === 'disable' && (
          <div>
            <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', fontSize: 13, marginBottom: 16 }}>
              Disabling 2FA reduces account security. Enter your password to confirm.
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: '#94a3b8' }}>Current Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && disableHandler()}
                style={inputStyle} autoFocus autoComplete="current-password" />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Button variant="secondary" onClick={() => { setStep('status'); setError(null); }} style={{ flex: 1 }}>Cancel</Button>
              <Button variant="danger" onClick={disableHandler} disabled={loading || !password} style={{ flex: 1 }}>
                {loading ? 'Disabling...' : 'Disable 2FA'}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

const ChangePasswordPage = ({ onBack }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const inputStyle = { width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 14, boxSizing: 'border-box' };
  const labelStyle = { display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500, color: '#94a3b8' };

  const handleSubmit = async () => {
    if (!currentPassword || !newPassword || !confirm) { setError('All fields are required'); return; }
    if (newPassword.length < 8) { setError('New password must be at least 8 characters'); return; }
    if (newPassword !== confirm) { setError('New passwords do not match'); return; }
    setLoading(true);
    setError(null);
    try {
      await adminApiCall('/admin/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      setSuccess(true);
      setCurrentPassword(''); setNewPassword(''); setConfirm('');
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ maxWidth: 440 }}>
      <Button variant="ghost" icon={ChevronLeft} onClick={onBack} style={{ marginBottom: 20 }}>Back</Button>
      <Card style={{ padding: 28 }}>
        <h2 style={{ margin: '0 0 6px', color: '#f8fafc', fontSize: 20, fontWeight: 700 }}>Change Password</h2>
        <p style={{ margin: '0 0 24px', color: '#64748b', fontSize: 13 }}>Update your Knine admin portal password.</p>

        {success && (
          <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', color: '#6ee7b7', fontSize: 13, marginBottom: 20 }}>
            Password changed successfully.
          </div>
        )}
        {error && (
          <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', fontSize: 13, marginBottom: 20 }}>
            {error}
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Current Password</label>
          <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} style={inputStyle} autoComplete="current-password" />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>New Password</label>
          <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={inputStyle} autoComplete="new-password" />
        </div>
        <div style={{ marginBottom: 24 }}>
          <label style={labelStyle}>Confirm New Password</label>
          <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} style={inputStyle} autoComplete="new-password"
            onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
        </div>

        <Button variant="primary" onClick={handleSubmit} disabled={loading} style={{ width: '100%' }}>
          {loading ? 'Updating...' : 'Update Password'}
        </Button>
      </Card>
    </div>
  );
};

const AdminApp = () => {
  const { admin, loading } = useAdmin();
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a' }}>
        <LoadingSpinner text="Loading admin portal..." />
      </div>
    );
  }

  if (!admin) {
    return <AdminLogin />;
  }

  return (
    <AdminLayout>
      {({ currentPage, setCurrentPage }) => (
        <>
          {currentPage === 'dashboard' && <DashboardOverview />}
          {currentPage === 'accounts' && <AccountsOverview />}
          {currentPage === 'customers' && (
            selectedCustomerId ? (
              <CustomerDetail
                customerId={selectedCustomerId}
                onBack={() => setSelectedCustomerId(null)}
              />
            ) : (
              <CustomersList onSelectCustomer={setSelectedCustomerId} />
            )
          )}
          {currentPage === 'audit' && <AuditLogsPage />}
          {currentPage === 'admins' && <AdminUsersPage />}
          {currentPage === 'recycleBin' && <RecycleBinPage />}
          {currentPage === 'hubspotSubs' && <HubSpotSubscriptionsPage />}
          {currentPage === 'consolidate' && <ConsolidatePage />}
          {currentPage === 'hubspotDuplicates' && <HubSpotDuplicatesPage />}
          {currentPage === 'changePassword' && (
            <ChangePasswordPage onBack={() => setCurrentPage('dashboard')} />
          )}
          {currentPage === 'twoFactor' && (
            <TwoFactorPage onBack={() => setCurrentPage('dashboard')} />
          )}
        </>
      )}
    </AdminLayout>
  );
};

// =============================================================================
// SEARCH LIMIT CARD — Editable org-level search limit settings
// =============================================================================

const SearchLimitCard = ({ title, description, isTrial, config, onSave, saving }) => {
  const [overrideLimit, setOverrideLimit] = useState(config.overrideLimit != null ? String(config.overrideLimit) : '');
  const [trialLimit, setTrialLimit] = useState(String(config.trialLimit));
  const [paidLimit, setPaidLimit] = useState(String(config.paidLimit));
  const [dirty, setDirty] = useState(false);

  const mark = () => setDirty(true);

  const handleSave = async () => {
    await onSave({
      overrideLimit: overrideLimit.trim() === '' ? null : parseInt(overrideLimit),
      trialLimit: parseInt(trialLimit) || undefined,
      paidLimit: parseInt(paidLimit) || undefined,
    });
    setDirty(false);
  };

  const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 13 };

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h4 style={{ margin: 0, color: '#f8fafc', fontWeight: 600 }}>{title}</h4>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748b' }}>{description}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Badge variant="info">Effective: {config.effectiveLimit}</Badge>
          {isTrial ? <Badge variant="warning">Trial</Badge> : <Badge variant="success">Paid</Badge>}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>
            Admin Override (blank = auto)
          </label>
          <input type="number" min="1" max="200" value={overrideLimit} onChange={e => { setOverrideLimit(e.target.value); mark(); }} placeholder="Auto" style={inputStyle} />
          <p style={{ margin: '3px 0 0', fontSize: 11, color: '#64748b' }}>Overrides trial/paid defaults completely</p>
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: '#f59e0b', fontWeight: 500 }}>
            Trial Default
          </label>
          <input type="number" min="1" max="200" value={trialLimit} onChange={e => { setTrialLimit(e.target.value); mark(); }} style={inputStyle} />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: '#10b981', fontWeight: 500 }}>
            Paid Default
          </label>
          <input type="number" min="1" max="200" value={paidLimit} onChange={e => { setPaidLimit(e.target.value); mark(); }} style={inputStyle} />
        </div>
      </div>

      {dirty && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Limits'}
          </Button>
        </div>
      )}
    </Card>
  );
};

// =============================================================================
// USER SEARCH OVERRIDE FORM — Inline form to set per-user limits
// =============================================================================

const UserSearchOverrideForm = ({ users, customerId, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [knineLimit, setKnineLimit] = useState('');
  const [cooperLimit, setCooperLimit] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (!selectedUser) { setError('Please select a user'); return; }
    setSaving(true);
    setError(null);
    try {
      await adminApiCall(`/admin/customers/${customerId}/search-config/users/${selectedUser}`, {
        method: 'PUT',
        body: JSON.stringify({
          knineResultLimit: knineLimit !== '' ? parseInt(knineLimit) : null,
          cooperResultLimit: cooperLimit !== '' ? parseInt(cooperLimit) : null,
          reason,
        }),
      });
      setOpen(false);
      setSelectedUser(''); setKnineLimit(''); setCooperLimit(''); setReason('');
      onSaved();
    } catch (err) {
      setError(err.message || 'Failed to save');
    }
    setSaving(false);
  };

  const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 13 };

  return (
    <>
      <Button variant="secondary" icon={Plus} onClick={() => setOpen(true)}>Add User Override</Button>
      {open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <Card style={{ padding: 24, width: 440 }}>
            <h4 style={{ margin: '0 0 16px', color: '#f8fafc' }}>Set Per-User Search Limit</h4>
            {error && <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', color: '#fca5a5', fontSize: 12, marginBottom: 12 }}>{error}</div>}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: '#94a3b8' }}>User</label>
              <select value={selectedUser} onChange={e => setSelectedUser(e.target.value)} style={inputStyle}>
                <option value="">Select a user...</option>
                {(users || []).map(u => (
                  <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: '#94a3b8' }}>kNine Limit (blank = org default)</label>
                <input type="number" min="1" max="200" value={knineLimit} onChange={e => setKnineLimit(e.target.value)} placeholder="Org default" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: '#94a3b8' }}>Cooper Limit (blank = org default)</label>
                <input type="number" min="1" max="200" value={cooperLimit} onChange={e => setCooperLimit(e.target.value)} placeholder="Org default" style={inputStyle} />
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: '#94a3b8' }}>Reason (optional)</label>
              <input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Power user, enterprise tier" style={inputStyle} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Button variant="secondary" onClick={() => setOpen(false)} style={{ flex: 1 }}>Cancel</Button>
              <Button variant="primary" onClick={handleSave} disabled={saving || !selectedUser} style={{ flex: 1 }}>
                {saving ? 'Saving...' : 'Save Override'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
};

// =============================================================================
// EXPORT
// =============================================================================

const AdminDashboard = () => (
  <AdminProvider>
    <AdminApp />
  </AdminProvider>
);

export default AdminDashboard;
